<template>
  <div class="p-2 sm:p-4 text-lexend overflow-x-hidden min-h-screen bg-gray-50 dark:bg-gray-900/50">
    <!-- Header Section -->
    <div class="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 sm:gap-4 mb-3 sm:mb-5 mt-1 scroll-reveal">
      <div class="animate-fadeIn w-full sm:w-auto">
        <div class="flex items-center gap-2 mb-1">
          <div class="w-8 h-1 bg-indigo-600 rounded-full"></div>
          <span class="text-[10px] sm:text-xs font-black text-indigo-600 tracking-widest">Panel de Control</span>
        </div>
        <h1 class="text-xl sm:text-3xl font-black text-gray-900 dark:text-white tracking-tight flex items-center flex-wrap gap-2">
          Lista de Recepciones
          <span class="px-2 sm:px-3 py-0.5 bg-indigo-100 dark:bg-indigo-900/40 text-indigo-600 dark:text-indigo-300 text-xs sm:text-sm font-black rounded-lg border border-indigo-200 dark:border-indigo-800">
            {{ recepcionesFiltradas.length }}
          </span>
          <span v-if="buscandoApi" class="text-[10px] text-indigo-500 font-black flex items-center gap-1">
            <Icon icon="mdi:loading" class="animate-spin" /> Buscando...
          </span>
          <span v-else-if="hayFiltrosActivos && recepcionesBusqueda !== null" class="text-[10px] text-emerald-600 dark:text-emerald-400 font-black flex items-center gap-1">
            <Icon icon="mdi:check-circle" /> {{ recepcionesBusqueda.length }} resultado(s)
          </span>
        </h1>
      </div>
      <!-- Botones de Excel -->
      <div class="flex items-center gap-2 w-full sm:w-auto">
        <button
          @click="exportarExcel"
          :disabled="recepciones.length === 0"
          class="px-3 sm:px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-xl font-black text-[10px] sm:text-xs tracking-widest transition-all shadow-md flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <Icon icon="mdi:file-excel" class="text-base sm:text-lg" />
          <span class="hidden sm:inline">Exportar Excel</span>
          <span class="sm:hidden">Excel</span>
        </button>
        <button
          @click="$refs.fileInput.click()"
          class="px-3 sm:px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-xl font-black text-[10px] sm:text-xs tracking-widest transition-all shadow-md flex items-center gap-2"
        >
          <Icon icon="mdi:file-upload" class="text-base sm:text-lg" />
          <span class="hidden sm:inline">Importar Excel</span>
          <span class="sm:hidden">Importar</span>
        </button>
        <input
          ref="fileInput"
          type="file"
          accept=".xlsx,.xls"
          class="hidden"
          @change="importarExcel($event)"
        />
      </div>
    </div>

    <!-- Filters & Search -->
    <div class="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-700 p-3 sm:p-5 mb-3 sm:mb-5 animate-fadeInUp">
      <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-12 gap-3 sm:gap-4 items-end">
        
        <!-- Filtro 1: N° Orden -->
        <div class="lg:col-span-2">
          <label class="block text-[10px] sm:text-[11px] font-black text-gray-400 tracking-widest mb-1 sm:mb-2 ml-1">
            <Icon icon="mdi:hash" class="inline mr-1 text-indigo-500" /> N° Orden
          </label>
          <div class="relative group">
            <Icon icon="mdi:hash-box-outline" class="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-lg group-focus-within:text-indigo-500 transition-colors" />
            <input
              v-model="filtroNumeroOrden"
              @keyup.enter="ejecutarBusquedaAvanzada"
              type="text"
              inputmode="numeric"
              placeholder="Ej: 7016"
              class="w-full pl-10 pr-9 py-2.5 sm:py-3 bg-gray-50 dark:bg-gray-900/50 border-2 border-gray-100 dark:border-gray-700 focus:border-indigo-500 focus:bg-white dark:focus:bg-gray-700 rounded-xl transition-all duration-300 outline-none dark:text-white font-bold text-sm"
            />
            <button v-if="filtroNumeroOrden" @click="filtroNumeroOrden = ''" class="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-gray-400 hover:text-red-500 transition-colors">
              <Icon icon="mdi:close-circle" class="text-base" />
            </button>
          </div>
        </div>

        <!-- Filtro 2: Cédula -->
        <div class="lg:col-span-2">
          <label class="block text-[10px] sm:text-[11px] font-black text-gray-400 tracking-widest mb-1 sm:mb-2 ml-1">
            <Icon icon="mdi:card-account-details" class="inline mr-1 text-blue-500" /> Cédula
          </label>
          <div class="relative group">
            <Icon icon="mdi:account-box-outline" class="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-lg group-focus-within:text-blue-500 transition-colors" />
            <input
              v-model="filtroCedula"
              @keyup.enter="ejecutarBusquedaAvanzada"
              type="text"
              inputmode="numeric"
              placeholder="Ej: 12345678"
              class="w-full pl-10 pr-9 py-2.5 sm:py-3 bg-gray-50 dark:bg-gray-900/50 border-2 border-gray-100 dark:border-gray-700 focus:border-blue-500 focus:bg-white dark:focus:bg-gray-700 rounded-xl transition-all duration-300 outline-none dark:text-white font-bold text-sm"
            />
            <button v-if="filtroCedula" @click="filtroCedula = ''" class="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-gray-400 hover:text-red-500 transition-colors">
              <Icon icon="mdi:close-circle" class="text-base" />
            </button>
          </div>
        </div>

        <!-- Filtro 3: Nombre / Apellido -->
        <div class="lg:col-span-3">
          <label class="block text-[10px] sm:text-[11px] font-black text-gray-400 tracking-widest mb-1 sm:mb-2 ml-1">
            <Icon icon="mdi:account-search" class="inline mr-1 text-emerald-500" /> Nombre / Cliente
          </label>
          <div class="relative group">
            <Icon icon="mdi:magnify" class="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-lg group-focus-within:text-emerald-500 transition-colors" />
            <input
              v-model="filtroNombre"
              @keyup.enter="ejecutarBusquedaAvanzada"
              type="text"
              placeholder="Ej: Juan Pérez"
              class="w-full pl-10 pr-9 py-2.5 sm:py-3 bg-gray-50 dark:bg-gray-900/50 border-2 border-gray-100 dark:border-gray-700 focus:border-emerald-500 focus:bg-white dark:focus:bg-gray-700 rounded-xl transition-all duration-300 outline-none dark:text-white font-bold text-sm"
            />
            <button v-if="filtroNombre" @click="filtroNombre = ''" class="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-gray-400 hover:text-red-500 transition-colors">
              <Icon icon="mdi:close-circle" class="text-base" />
            </button>
          </div>
        </div>

        <!-- Filtro 4: Producto / Equipo / Falla -->
        <div class="lg:col-span-3">
          <label class="block text-[10px] sm:text-[11px] font-black text-gray-400 tracking-widest mb-1 sm:mb-2 ml-1">
            <Icon icon="mdi:cellphone-link" class="inline mr-1 text-purple-500" /> Equipo / Producto
          </label>
          <div class="relative group">
            <Icon icon="mdi:package-variant-closed" class="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-lg group-focus-within:text-purple-500 transition-colors" />
            <input
              v-model="filtroProducto"
              @keyup.enter="ejecutarBusquedaAvanzada"
              type="text"
              placeholder="Ej: Pantalla, iPhone, Laptop..."
              class="w-full pl-10 pr-9 py-2.5 sm:py-3 bg-gray-50 dark:bg-gray-900/50 border-2 border-gray-100 dark:border-gray-700 focus:border-purple-500 focus:bg-white dark:focus:bg-gray-700 rounded-xl transition-all duration-300 outline-none dark:text-white font-bold text-sm"
            />
            <button v-if="filtroProducto" @click="filtroProducto = ''" class="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-gray-400 hover:text-red-500 transition-colors">
              <Icon icon="mdi:close-circle" class="text-base" />
            </button>
          </div>
        </div>

        <!-- Filtro Estado -->
        <div class="sm:col-span-1 lg:col-span-1">
          <label class="block text-[10px] sm:text-[11px] font-black text-gray-400 tracking-widest mb-1 sm:mb-2 ml-1">Estado</label>
          <select
            v-model="filtroEstado"
            class="w-full px-2 py-2.5 sm:py-3 bg-gray-50 dark:bg-gray-900/50 border-2 border-gray-100 dark:border-gray-700 focus:border-indigo-500 rounded-xl font-black text-gray-800 dark:text-white outline-none transition-all appearance-none cursor-pointer text-xs"
          >
            <option value="TODOS">TODOS</option>
            <option value="EXPRESS">EXPRESS</option>
            <option v-for="est in estadosList" :key="est.idEstadoRecepcion" :value="est.nombre">{{ est.nombre?.toUpperCase() }}</option>
          </select>
        </div>

        <!-- Botón Refrescar -->
        <div class="sm:col-span-1 lg:col-span-1">
          <button 
            @click="refrescarTodo"
            :disabled="buscandoApi || loading"
            class="w-full py-2.5 sm:py-3.5 bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 rounded-xl font-black text-[10px] sm:text-xs tracking-widest hover:bg-gray-200 dark:hover:bg-gray-600 transition-all flex items-center justify-center gap-2 border border-gray-200 dark:border-gray-600 shadow-sm disabled:opacity-50"
            title="Refrescar datos"
          >
            <Icon :icon="(buscandoApi || loading) ? 'mdi:loading' : 'mdi:refresh'" :class="{'animate-spin': buscandoApi || loading}" class="text-lg" />
          </button>
        </div>
      </div>

      <!-- Chips de filtros activos -->
      <div v-if="hayFiltrosActivos" class="mt-3 pt-3 border-t border-gray-100 dark:border-gray-700 flex flex-wrap items-center gap-2">
        <span class="text-[10px] font-black text-gray-400 uppercase tracking-widest">Filtros activos:</span>
        
        <span v-if="filtroNumeroOrden" class="px-2 py-1 bg-indigo-50 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-300 rounded-lg text-[10px] font-black flex items-center gap-1">
          <Icon icon="mdi:hash" /> {{ filtroNumeroOrden }}
          <button @click="filtroNumeroOrden = ''" class="ml-1 hover:text-red-500"><Icon icon="mdi:close" /></button>
        </span>
        
        <span v-if="filtroCedula" class="px-2 py-1 bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 rounded-lg text-[10px] font-black flex items-center gap-1">
          <Icon icon="mdi:card-account-details" /> {{ filtroCedula }}
          <button @click="filtroCedula = ''" class="ml-1 hover:text-red-500"><Icon icon="mdi:close" /></button>
        </span>
        
        <span v-if="filtroNombre" class="px-2 py-1 bg-emerald-50 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300 rounded-lg text-[10px] font-black flex items-center gap-1">
          <Icon icon="mdi:account-search" /> {{ filtroNombre }}
          <button @click="filtroNombre = ''" class="ml-1 hover:text-red-500"><Icon icon="mdi:close" /></button>
        </span>

        <span v-if="filtroProducto" class="px-2 py-1 bg-purple-50 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300 rounded-lg text-[10px] font-black flex items-center gap-1">
          <Icon icon="mdi:cellphone-link" /> {{ filtroProducto }}
          <button @click="filtroProducto = ''" class="ml-1 hover:text-red-500"><Icon icon="mdi:close" /></button>
        </span>

        <button @click="limpiarFiltros" class="ml-auto px-3 py-1 bg-red-50 dark:bg-red-900/30 text-red-600 dark:text-red-400 rounded-lg text-[10px] font-black uppercase tracking-widest hover:bg-red-600 hover:text-white transition-all flex items-center gap-1">
          <Icon icon="mdi:filter-remove" /> Limpiar
        </button>
      </div>
    </div>

    <!-- Data Table -->
    <div class="bg-white dark:bg-gray-800 rounded-2xl shadow-md border border-gray-200 dark:border-gray-700 overflow-hidden animate-fadeInUp delay-100">
      <!-- Versión Desktop: Tabla completa -->
      <div class="hidden lg:block overflow-x-auto custom-scrollbar">
        <table class="w-full text-left border-collapse">
          <thead>
            <tr class="bg-gray-50 dark:bg-gray-900/50 border-b border-gray-200 dark:border-gray-700">
              <th class="px-5 py-4 text-[11px] font-black text-gray-400 tracking-widest">Orden</th>
              <th class="px-5 py-4 text-[11px] font-black text-gray-400 tracking-widest">Equipo / Problema</th>
              <th class="px-5 py-4 text-[11px] font-black text-gray-400 tracking-widest text-center">Acciones</th>
              <th class="px-5 py-4 text-[11px] font-black text-gray-400 tracking-widest">Cliente</th>
              <th class="px-5 py-4 text-[11px] font-black text-gray-400 tracking-widest">Fecha Ingreso</th>
              <th class="px-5 py-4 text-[11px] font-black text-gray-400 tracking-widest text-center">Estado</th>
              <th class="px-5 py-4 text-[11px] font-black text-gray-400 tracking-widest">Total</th>
              <th class="px-5 py-4 text-[11px] font-black text-gray-400 tracking-widest text-left">Personal</th>
              <th class="px-5 py-4 text-[11px] font-black text-gray-400 tracking-widest">Diagnóstico</th>
              <th class="px-5 py-4 text-[11px] font-black text-gray-400 tracking-widest">Sede</th>
            </tr>
          </thead>
          <tbody class="divide-y divide-gray-100 dark:divide-gray-700">
            <tr v-if="loading || buscandoApi" v-for="i in 5" :key="'load-'+i" class="animate-pulse">
              <td colspan="10" class="px-5 py-4"><div class="h-4 bg-gray-100 dark:bg-gray-700 rounded w-full"></div></td>
            </tr>
            <tr v-if="mostrarEstadoVacio">
              <td colspan="10" class="px-5 py-16 text-center">
                <div class="flex flex-col items-center opacity-30 select-none">
                  <Icon icon="mdi:database-off-outline" class="text-7xl mb-3" />
                  <h4 class="text-xl font-black text-gray-400 tracking-widest">No hay registros</h4>
                  <p class="text-sm text-gray-400 mt-2" v-if="hayFiltrosActivos">Ajusta los filtros de búsqueda</p>
                </div>
              </td>
            </tr>
            <tr v-for="item in paginatedRecepciones" :key="item.idRecepcionEquipo" class="hover:bg-indigo-50/30 dark:hover:bg-gray-700/30 transition-all group">
              <td class="px-5 py-4">
                <span class="px-2.5 py-1 bg-indigo-50 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-300 rounded-lg font-black text-xs border border-indigo-100 dark:border-indigo-800 transition-transform inline-block">
                  #{{ item.numeroOrden }}
                </span>
              </td>
              <td class="px-5 py-4">
                <div class="text-left">
                  <p class="font-black text-gray-900 dark:text-white text-xs mb-1 truncate max-w-[150px]">{{ item.descripcion }}</p>
                  <p class="text-xs text-gray-500 dark:text-gray-400 flex items-center gap-1 italic max-w-[150px] truncate" :title="item.problema">
                    {{ item.problema }}
                  </p>
                </div>
              </td>
              <td class="px-5 py-4">
                <div class="flex items-center justify-center gap-0.5 flex-nowrap whitespace-nowrap">
                  <button
                    @click="abrirWhatsappCliente(item)"
                    title="Enviar mensaje por WhatsApp"
                    class="p-1.5 bg-green-50 dark:bg-green-900/30 text-green-600 dark:text-green-400 rounded-lg hover:bg-green-600 hover:text-white transition-all shadow-sm flex-shrink-0"
                  >
                    <Icon icon="mdi:whatsapp" class="text-base" />
                  </button>

                  <button
                    @click="generarPDFRecepcion(item)"
                    title="Generar PDF Formato Carta"
                    class="p-1.5 bg-red-50 dark:bg-red-900/30 text-red-600 dark:text-red-400 rounded-lg hover:bg-red-600 hover:text-white transition-all shadow-sm flex-shrink-0"
                  >
                    <Icon icon="mdi:file-pdf-box" class="text-base" />
                  </button>

                  <button
                    v-if="item.nombreEstado?.toLowerCase() !== 'entregado'"
                    @click="abrirEditarModal(item)"
                    title="Editar Accesorios y Sede"
                    class="p-1.5 bg-violet-50 dark:bg-violet-900/30 text-violet-600 dark:text-violet-400 rounded-lg hover:bg-violet-600 hover:text-white transition-all shadow-sm flex-shrink-0"
                  >
                    <Icon icon="mdi:pencil" class="text-base" />
                  </button>

                  <button 
                    @click="verDetallesRecepcion(item)"
                    title="Ver Detalle"
                    class="p-1.5 bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 rounded-lg hover:bg-blue-600 hover:text-white transition-all shadow-sm flex-shrink-0"
                  >
                    <Icon icon="mdi:eye" class="text-base" />
                  </button>
                  
                  <button 
                    @click="imprimirTicketLocal(item)"
                    title="Imprimir Ticket"
                    class="p-1.5 bg-amber-50 dark:bg-amber-900/30 text-amber-600 dark:text-amber-400 rounded-lg hover:bg-amber-600 hover:text-white transition-all shadow-sm flex-shrink-0"
                  >
                    <Icon icon="mdi:printer" class="text-base" />
                  </button>
                  
                  <template v-if="item.nombreEstado?.toUpperCase() !== 'ENTREGADO'">
                    <button 
                      @click="$router.push(`/seguimiento/${item.numeroOrden}?tab=2`)"
                      title="Reparar / Editar Reparación"
                      class="p-1.5 bg-indigo-50 dark:bg-indigo-900/30 text-indigo-600 dark:text-indigo-400 rounded-lg hover:bg-indigo-600 hover:text-white transition-all shadow-sm flex-shrink-0"
                    >
                      <Icon icon="mdi:tools" class="text-base" />
                    </button>

                    <button 
                      v-if="item.nombreEstado?.toLowerCase().includes('imposible')"
                      @click="entregarSinPago(item)"
                      title="Entregar sin Cobro"
                      class="p-1.5 bg-orange-50 dark:bg-orange-900/30 text-orange-600 dark:text-orange-400 rounded-lg hover:bg-orange-600 hover:text-white transition-all shadow-sm flex-shrink-0"
                    >
                      <Icon icon="mdi:truck-delivery" class="text-base" />
                    </button>

                    <button 
                      v-if="item.nombreEstado?.toLowerCase().includes('garant')"
                      @click="finalizarGarantia(item)"
                      title="Finalizar y Entregar Garantía"
                      class="p-1.5 bg-indigo-50 dark:bg-indigo-900/30 text-indigo-600 dark:text-indigo-400 rounded-lg hover:bg-indigo-600 hover:text-white transition-all shadow-sm flex-shrink-0"
                    >
                      <Icon icon="mdi:check-circle-outline" class="text-base" />
                    </button>

                    <!-- ✅ FIX: Botón Registrar Pago / Abono Online — OCULTO si está "Por Diagnosticar" -->
                    <button 
                      v-if="!esTotalmentePagado(item) && !esEstadoPorDiagnosticar(item) && !esSinCosto(item)"
                      @click="abrirModalPago(item)"
                      :title="tieneAbono(item) ? 'Completar Pago / Registrar Abono' : 'Registrar Pago / Pago Online'"
                      class="p-1.5 bg-emerald-50 dark:bg-emerald-900/30 text-emerald-600 dark:text-emerald-400 rounded-lg hover:bg-emerald-600 hover:text-white transition-all shadow-sm flex-shrink-0"
                    >
                      <Icon icon="mdi:cash-plus" class="text-base" />
                    </button>

                    <!-- ✅ Indicador de Pago Online Pendiente de Verificación -->
                    <span 
                      v-if="esPagoTotalmentePendienteVerificar(item)"
                      title="Pago online pendiente de verificación en /pagos"
                      class="p-1.5 bg-amber-100 dark:bg-amber-950/60 text-amber-700 dark:text-amber-300 rounded-lg flex-shrink-0 inline-flex items-center justify-center cursor-default"
                    >
                      <Icon icon="mdi:clock-alert-outline" class="text-base" />
                    </span>

                    <!-- ✅ Indicador Pagado 100% Verificado -->
                    <span 
                      v-else-if="esTotalmentePagado(item) && !esSinCosto(item)"
                      title="Orden Pagada al 100%"
                      class="p-1.5 bg-emerald-100 dark:bg-emerald-950/60 text-emerald-600 dark:text-emerald-400 rounded-lg flex-shrink-0 inline-flex items-center justify-center cursor-default"
                    >
                      <Icon icon="mdi:check-decagram" class="text-base" />
                    </span>

                    <!-- Botón Dar Salida: visible cuando está REPARADO, pagado al 100% y verificado en caja/pagos -->
                    <button
                      v-if="item.nombreEstado?.toLowerCase() === 'reparado' && esTotalmentePagado(item) && !esPagoTotalmentePendienteVerificar(item)"
                      @click="entregarSinPago(item)"
                      title="Dar Salida / Entregar Equipo"
                      class="p-1.5 bg-teal-50 dark:bg-teal-900/30 text-teal-600 dark:text-teal-400 rounded-lg hover:bg-teal-600 hover:text-white transition-all shadow-sm flex-shrink-0"
                    >
                      <Icon icon="mdi:truck-fast" class="text-base" />
                    </button>

                    <button 
                      v-if="item.nombreEstado?.toLowerCase() === 'reparado' && !esTotalmentePagado(item)"
                      @click="$router.push(`/seguimiento/${item.numeroOrden}?tab=3`)"
                      title="Ir a Entrega y Cobro"
                      class="p-1.5 bg-teal-50 dark:bg-teal-900/30 text-teal-600 dark:text-teal-400 rounded-lg hover:bg-teal-600 hover:text-white transition-all shadow-sm flex-shrink-0"
                    >
                      <Icon icon="mdi:truck-delivery-outline" class="text-base" />
                    </button>

                    <button
                      v-if="esPorDiagnosticar(item)"
                      @click="abrirPagoExpress(item)"
                      title="Pasar a Diagnóstico Express ($10)"
                      class="p-1.5 bg-yellow-50 dark:bg-yellow-900/30 text-yellow-600 dark:text-yellow-300 rounded-lg hover:bg-yellow-600 hover:text-white transition-all shadow-sm flex-shrink-0"
                    >
                      <Icon icon="mdi:flash" class="text-base" />
                    </button>
                  </template>
                  
                  <div v-else class="flex items-center gap-0.5 flex-shrink-0">
                    <div class="px-2 py-1 bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-400 rounded-lg border border-green-200 dark:border-green-800 text-[10px] font-black uppercase flex items-center gap-1 shadow-sm" title="Orden Completada">
                      <Icon icon="mdi:check-decagram" class="text-base" />
                    </div>

                    <button 
                      @click="ponerEnGarantia(item)"
                      title="Pasar a Garantía"
                      class="p-1.5 bg-rose-50 dark:bg-rose-900/30 text-rose-600 dark:text-rose-400 rounded-lg hover:bg-rose-600 hover:text-white transition-all shadow-sm flex-shrink-0"
                    >
                      <Icon icon="mdi:shield-alert" class="text-base" />
                    </button>
                  </div>

                  <button
                    v-if="esAdmin"
                    @click="eliminarRegistro(item)"
                    title="Eliminar Permanente (Administrador)"
                    class="p-1.5 bg-red-50 dark:bg-red-900/30 text-red-600 dark:text-red-400 rounded-lg hover:bg-red-600 hover:text-white transition-all shadow-sm flex-shrink-0"
                  >
                    <Icon icon="mdi:trash-can-outline" class="text-base" />
                  </button>
                </div>
              </td>
              <td class="px-5 py-4">
                <div class="flex items-center gap-3 text-left">
                  <div class="w-8 h-8 bg-gray-100 dark:bg-gray-700 rounded-lg flex items-center justify-center font-black text-gray-500 text-xs">
                    {{ item.nombreCliente?.charAt(0) }}
                  </div>
                  <div>
                    <p class="font-black text-gray-900 dark:text-white text-xs leading-tight">{{ item.nombreCliente }}</p>
                    <p class="text-[10px] font-black text-gray-400 uppercase tracking-widest">ID: {{ item.documentoCliente || 'S/D' }}</p>
                  </div>
                </div>
              </td>
              <td class="px-5 py-4 whitespace-nowrap">
                <div class="text-left space-y-1">
                  <div>
                    <p class="font-black text-gray-900 dark:text-white text-[10px] leading-none mb-0.5">ING: {{ formatearFecha(item.fechaIngreso) }}</p>
                    <p class="text-[9px] text-gray-400 font-black uppercase">{{ formatearHora(item.fechaIngreso) }}</p>
                  </div>
                  <div v-if="item.fechaReparacion" class="pt-1 border-t border-gray-100 dark:border-gray-700 animate-fadeIn">
                    <p class="font-black text-indigo-600 dark:text-indigo-400 text-[10px] leading-none mb-0.5">REP: {{ formatearFecha(item.fechaReparacion) }}</p>
                    <p class="text-[9px] text-indigo-400 font-black uppercase">{{ formatearHora(item.fechaReparacion) }}</p>
                  </div>
                </div>
              </td>
              <td class="px-5 py-4">
                <div :class="getEstadoStyle(item.nombreEstado)" class="px-3 py-1 rounded-lg text-[10px] font-black uppercase tracking-widest text-center border shadow-sm transition-all inline-block">
                  {{ item.nombreEstado }}
                </div>
              </td>
              <td class="px-5 py-4">
                <div v-if="debeMostrarTotal(item)" class="flex flex-col">
                  <span :class="{'line-through text-gray-400 text-[10px]': item.descuento > 0}" class="font-black text-gray-900 dark:text-white text-xs">
                    ${{ (item.precioTotalUsd || 0).toFixed(2) }}
                  </span>
                  <div v-if="item.descuento > 0" class="flex flex-col animate-fadeIn mt-1">
                    <span class="text-[9px] font-black text-rose-500 uppercase tracking-tighter">
                      DESC: -${{ item.descuento.toFixed(2) }}
                    </span>
                  </div>
                  <div v-if="item.montoPagadoDiagnosticoUsd > 0" class="flex flex-col mt-1">
                    <span class="text-[10px] font-black text-emerald-600 dark:text-emerald-400 uppercase tracking-widest leading-none">
                      EXPRESS:
                    </span>
                    <span class="text-xs font-black text-emerald-700 dark:text-emerald-300">
                      ${{ (item.montoPagadoDiagnosticoUsd || 0).toFixed(2) }}
                    </span>
                  </div>
                  <div v-if="item.montoPagadoReparacionUsd > 0" class="flex flex-col mt-1">
                    <span class="text-[10px] font-black text-blue-600 dark:text-blue-400 uppercase tracking-widest leading-none">
                      {{ (item.metodoPago?.toUpperCase().includes('CASHEA') || item.montoPagadoReparacionUsd < (item.precioTotalUsd - item.descuento)) ? 'Inicial:' : 'Pagado:' }}
                    </span>
                    <span class="text-xs font-black text-blue-700 dark:text-blue-300">
                      ${{ (item.montoPagadoReparacionUsd || 0).toFixed(2) }}
                    </span>
                  </div>

                  <!-- ✅ BADGES DE ESTADO DE PAGO -->
                  <div v-if="esPagoTotalmentePendienteVerificar(item)" class="mt-1">
                    <span @click.stop="$router.push('/pagos')" class="px-2 py-0.5 bg-amber-100 dark:bg-amber-950/70 text-amber-800 dark:text-amber-200 border border-amber-300 dark:border-amber-700 rounded-md text-[9px] font-black uppercase tracking-wider inline-flex items-center gap-1 shadow-xs cursor-pointer hover:bg-amber-200 dark:hover:bg-amber-900 transition-all hover:scale-105" title="Haz clic para verificar este pago en /pagos">
                      <Icon icon="mdi:clock-alert-outline" class="text-xs text-amber-600 dark:text-amber-400 animate-pulse" />
                      PAGADO 100% (DEBE DE VERIFICAR)
                    </span>
                  </div>
                  <div v-else-if="esTotalmentePagado(item)" class="mt-1">
                    <span class="px-2 py-0.5 bg-emerald-100 dark:bg-emerald-950/70 text-emerald-700 dark:text-emerald-300 border border-emerald-300 dark:border-emerald-800 rounded-md text-[9px] font-black uppercase tracking-wider inline-flex items-center gap-1 shadow-xs">
                      <Icon icon="mdi:check-decagram" class="text-xs text-emerald-600 dark:text-emerald-400" />
                      {{ esPagoAdelantado(item) ? 'PAGADO 100% (ADELANTADO)' : 'PAGADO 100%' }}
                    </span>
                  </div>
                  <div v-else-if="tieneAbono(item)" class="mt-1 flex flex-col gap-0.5">
                    <span class="px-1.5 py-0.5 bg-amber-100 dark:bg-amber-950/70 text-amber-700 dark:text-amber-300 border border-amber-300 dark:border-amber-800 rounded-md text-[9px] font-black uppercase tracking-wider inline-flex items-center gap-1 shadow-xs">
                      <Icon icon="mdi:cash-clock" class="text-xs text-amber-600 dark:text-amber-400" />
                      ABONADO: ${{ obtenerMontoPagado(item).toFixed(2) }}
                    </span>
                    <span class="text-[9px] font-black text-rose-600 dark:text-rose-400 pl-0.5">
                      Resta: ${{ obtenerSaldoRestante(item).toFixed(2) }}
                    </span>
                  </div>
                  <div v-else-if="obtenerTotalNeto(item) > 0" class="mt-1">
                    <span class="px-1.5 py-0.5 bg-rose-50 dark:bg-rose-950/40 text-rose-600 dark:text-rose-400 border border-rose-200 dark:border-rose-800 rounded-md text-[9px] font-bold uppercase tracking-wider inline-flex items-center gap-1">
                      <Icon icon="mdi:clock-alert-outline" class="text-xs" />
                      PENDIENTE
                    </span>
                  </div>
                </div>
                <div v-else class="text-gray-400 dark:text-gray-600 font-bold text-xs select-none">—</div>
              </td>
              <td class="px-5 py-4">
                <div class="flex flex-col gap-1.5 min-w-[120px]">
                  <div class="flex items-center gap-1.5">
                    <Icon icon="mdi:account-arrow-right" class="text-xs text-blue-500" />
                    <span class="text-[9px] font-bold text-gray-600 dark:text-gray-300 truncate">{{ item.nombreTecnicoRecepcion || item.NombreTecnicoRecepcion || item.nombreUsuario || item.NombreUsuario || 'Recepción' }}</span>
                  </div>
                  <div v-if="item.nombreTecnicoReparador || item.NombreTecnicoReparador" class="flex items-center gap-1.5">
                    <Icon icon="mdi:hammer-wrench" class="text-xs text-yellow-500" />
                    <span class="text-[9px] font-bold text-gray-600 dark:text-gray-300 truncate">{{ item.nombreTecnicoReparador || item.NombreTecnicoReparador }}</span>
                  </div>
                  <div v-if="item.nombreTecnicoEntrega || item.NombreTecnicoEntrega" class="flex items-center gap-1.5">
                    <Icon icon="mdi:truck-delivery" class="text-xs text-emerald-500" />
                    <span class="text-[9px] font-bold text-gray-600 dark:text-gray-300 truncate">{{ item.nombreTecnicoEntrega || item.NombreTecnicoEntrega }}</span>
                  </div>
                </div>
              </td>
              <td class="px-5 py-4">
                <div :class="[
                  'px-3 py-1 rounded-full text-[10px] font-black text-center border inline-flex items-center gap-1.5 uppercase tracking-widest',
                  ((item.tipoDiagnostico || item.TipoDiagnostico || '').toUpperCase().includes('EXPRE')) 
                    ? 'bg-rose-50 text-rose-700 border-rose-100 dark:bg-rose-900/30 dark:border-rose-800'
                    : ((item.tipoDiagnostico || item.TipoDiagnostico || '').toUpperCase().includes('ADELANTA'))
                      ? 'bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-950/50 dark:border-emerald-800'
                      : 'bg-indigo-50 text-indigo-700 border-indigo-100 dark:bg-indigo-900/30 dark:border-indigo-800'
                ]">
                  <Icon :icon="((item.tipoDiagnostico || item.TipoDiagnostico || '').toUpperCase().includes('EXPRE')) ? 'mdi:flash' : ((item.tipoDiagnostico || item.TipoDiagnostico || '').toUpperCase().includes('ADELANTA')) ? 'mdi:cash-check' : 'mdi:heart-pulse'" class="text-xs" />
                  {{ (item.tipoDiagnostico || item.TipoDiagnostico || 'GRATIS') }}
                </div>
              </td>
              <td class="px-5 py-4">
                <div class="flex items-center gap-3">
                  <Icon icon="mdi:store-marker" class="text-amber-500 text-xl" />
                  <div>
                    <p class="font-black text-gray-900 dark:text-white text-xs leading-tight">{{ item.nombreSede || item.taller || 'Sede Central' }}</p>
                    <p class="text-[9px] font-black text-gray-400 uppercase tracking-widest">UBICACIÓN</p>
                  </div>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- Versión Mobile: Tarjetas -->
      <div class="lg:hidden space-y-3 p-2">
        <div v-if="loading || buscandoApi" v-for="i in 3" :key="'load-mobile-'+i" class="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-700 p-4 animate-pulse">
          <div class="h-4 bg-gray-100 dark:bg-gray-700 rounded w-full mb-2"></div>
          <div class="h-3 bg-gray-100 dark:bg-gray-700 rounded w-3/4"></div>
        </div>
        
        <div v-if="mostrarEstadoVacio" class="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-700 p-8 text-center">
          <div class="flex flex-col items-center opacity-30 select-none">
            <Icon icon="mdi:database-off-outline" class="text-6xl mb-3" />
            <h4 class="text-lg font-black text-gray-400 tracking-widest">No hay registros</h4>
            <p class="text-sm text-gray-400 mt-2" v-if="hayFiltrosActivos">Ajusta los filtros de búsqueda</p>
          </div>
        </div>
        
        <div v-for="item in paginatedRecepciones" :key="item.idRecepcionEquipo" 
             class="bg-white dark:bg-gray-800 rounded-2xl shadow-md border border-gray-200 dark:border-gray-700 p-4 hover:shadow-lg transition-all duration-200">
          
          <div class="flex flex-wrap items-center justify-between gap-2 mb-3 pb-2 border-b border-gray-100 dark:border-gray-700">
            <div class="flex items-center gap-2">
              <span class="px-2.5 py-1 bg-indigo-50 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-300 rounded-lg font-black text-xs border border-indigo-100 dark:border-indigo-800">
                #{{ item.numeroOrden }}
              </span>
              <div :class="getEstadoStyle(item.nombreEstado)" class="px-2 py-0.5 rounded-lg text-[8px] font-black uppercase tracking-widest border shadow-sm">
                {{ item.nombreEstado }}
              </div>
              <span v-if="esPagoTotalmentePendienteVerificar(item)" class="px-2 py-0.5 bg-amber-100 dark:bg-amber-950/70 text-amber-800 dark:text-amber-200 border border-amber-300 dark:border-amber-700 rounded-md text-[8px] font-black uppercase inline-flex items-center gap-0.5 shadow-xs">
                <Icon icon="mdi:clock-alert-outline" class="text-[10px]" />
                PAGO 100% (POR VERIFICAR)
              </span>
              <span v-else-if="esTotalmentePagado(item)" class="px-2 py-0.5 bg-emerald-100 dark:bg-emerald-950/70 text-emerald-700 dark:text-emerald-300 border border-emerald-300 dark:border-emerald-800 rounded-md text-[8px] font-black uppercase inline-flex items-center gap-0.5 shadow-xs">
                <Icon icon="mdi:check-decagram" class="text-[10px]" />
                {{ esPagoAdelantado(item) ? 'PAGADO 100%' : 'PAGADO' }}
              </span>
              <span v-else-if="tieneAbono(item)" class="px-2 py-0.5 bg-amber-100 dark:bg-amber-950/70 text-amber-700 dark:text-amber-300 border border-amber-300 dark:border-amber-800 rounded-md text-[8px] font-black uppercase inline-flex items-center gap-0.5 shadow-xs">
                <Icon icon="mdi:cash-clock" class="text-[10px]" />
                ABONADO
              </span>
            </div>
            <div class="text-[9px] text-gray-400 font-black">
              {{ formatearFecha(item.fechaIngreso) }}
            </div>
          </div>

          <div class="flex items-center gap-2 mb-2">
            <div class="w-7 h-7 bg-gray-100 dark:bg-gray-700 rounded-lg flex items-center justify-center font-black text-gray-500 text-xs flex-shrink-0">
              {{ item.nombreCliente?.charAt(0) }}
            </div>
            <div class="flex-1 min-w-0">
              <p class="font-black text-gray-900 dark:text-white text-xs leading-tight truncate">{{ item.nombreCliente }}</p>
              <p class="text-[8px] font-black text-gray-400 uppercase tracking-widest">ID: {{ item.documentoCliente || 'S/D' }}</p>
            </div>
          </div>

          <div class="mb-2 bg-gray-50 dark:bg-gray-900/30 rounded-xl p-2">
            <p class="font-black text-gray-900 dark:text-white text-xs truncate">{{ item.descripcion }}</p>
            <p class="text-[10px] text-gray-500 dark:text-gray-400 italic truncate">{{ item.problema }}</p>
          </div>

          <div class="grid grid-cols-2 gap-1 mb-2 text-[10px]">
            <div class="flex items-center gap-1">
              <Icon icon="mdi:store-marker" class="text-amber-500 text-sm" />
              <span class="font-bold text-gray-600 dark:text-gray-400 truncate">{{ item.nombreSede || item.taller || 'Sede Central' }}</span>
            </div>
            <div class="flex items-center gap-1 justify-end">
              <div :class="[
                'px-2 py-0.5 rounded-full text-[8px] font-black text-center border inline-flex items-center gap-1 uppercase tracking-widest',
                ((item.tipoDiagnostico || item.TipoDiagnostico || '').toUpperCase().includes('EXPRE')) 
                  ? 'bg-rose-50 text-rose-700 border-rose-100 dark:bg-rose-900/30 dark:border-rose-800'
                  : ((item.tipoDiagnostico || item.TipoDiagnostico || '').toUpperCase().includes('ADELANTA'))
                    ? 'bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-950/50 dark:border-emerald-800'
                    : 'bg-emerald-50 text-emerald-700 border-emerald-100 dark:bg-emerald-900/30 dark:border-emerald-800'
              ]">
                <Icon :icon="((item.tipoDiagnostico || item.TipoDiagnostico || '').toUpperCase().includes('EXPRE')) ? 'mdi:flash' : ((item.tipoDiagnostico || item.TipoDiagnostico || '').toUpperCase().includes('ADELANTA')) ? 'mdi:cash-check' : 'mdi:heart-pulse'" class="text-[10px]" />
                {{ (item.tipoDiagnostico || item.TipoDiagnostico || 'GRATIS') }}
              </div>
            </div>
          </div>

          <div v-if="debeMostrarTotal(item)" class="flex flex-wrap items-center gap-2 mb-2 text-xs bg-gray-50 dark:bg-gray-900/30 rounded-xl p-2">
            <span class="font-black text-gray-900 dark:text-white">${{ (item.precioTotalUsd || 0).toFixed(2) }}</span>
            <span v-if="item.descuento > 0" class="text-[9px] font-black text-rose-500 line-through">-${{ item.descuento.toFixed(2) }}</span>
            <span v-if="item.montoPagadoDiagnosticoUsd > 0" class="text-[9px] font-black text-emerald-600">EXPRESS: ${{ (item.montoPagadoDiagnosticoUsd || 0).toFixed(2) }}</span>
            <span v-if="item.montoPagadoReparacionUsd > 0" class="text-[9px] font-black text-blue-600">PAGO: ${{ (item.montoPagadoReparacionUsd || 0).toFixed(2) }}</span>
            <span v-if="esPagoTotalmentePendienteVerificar(item)" @click.stop="$router.push('/pagos')" class="ml-auto px-2 py-0.5 bg-amber-100 dark:bg-amber-950/70 text-amber-800 dark:text-amber-200 rounded font-black text-[9px] flex items-center gap-0.5 cursor-pointer hover:bg-amber-200 dark:hover:bg-amber-900 transition-colors" title="Haz clic para verificar en /pagos">
              <Icon icon="mdi:clock-alert-outline" class="text-xs animate-pulse" /> POR VERIFICAR
            </span>
            <span v-else-if="esTotalmentePagado(item)" class="ml-auto px-2 py-0.5 bg-emerald-100 dark:bg-emerald-950/70 text-emerald-700 dark:text-emerald-300 rounded font-black text-[9px] flex items-center gap-0.5">
              <Icon icon="mdi:check-decagram" class="text-xs" /> PAGADO
            </span>
            <span v-else-if="tieneAbono(item)" class="ml-auto px-1.5 py-0.5 bg-amber-100 dark:bg-amber-950/70 text-amber-700 dark:text-amber-300 rounded font-black text-[9px] flex items-center gap-0.5">
              <Icon icon="mdi:cash-clock" class="text-xs" /> ABONADO: ${{ obtenerMontoPagado(item).toFixed(2) }}
            </span>
          </div>

          <div class="flex flex-wrap items-center gap-2 mb-3 text-[9px] bg-gray-50 dark:bg-gray-900/30 rounded-xl p-2">
            <div class="flex items-center gap-1">
              <Icon icon="mdi:account-arrow-right" class="text-blue-500 text-sm" />
              <span class="font-bold text-gray-600 dark:text-gray-400 truncate">{{ item.nombreTecnicoRecepcion || item.NombreTecnicoRecepcion || 'Recepción' }}</span>
            </div>
            <div v-if="item.nombreTecnicoReparador || item.NombreTecnicoReparador" class="flex items-center gap-1">
              <Icon icon="mdi:hammer-wrench" class="text-yellow-500 text-sm" />
              <span class="font-bold text-gray-600 dark:text-gray-400 truncate">{{ item.nombreTecnicoReparador || item.NombreTecnicoReparador }}</span>
            </div>
          </div>

          <div class="flex flex-wrap items-center gap-1.5 pt-2 border-t border-gray-200 dark:border-gray-700">
            <div class="flex items-center gap-1 mr-1">
              <button
                @click="abrirWhatsappCliente(item)"
                title="WhatsApp"
                class="p-1.5 bg-green-50 dark:bg-green-900/30 text-green-600 dark:text-green-400 rounded-lg hover:bg-green-600 hover:text-white transition-all shadow-sm"
              >
                <Icon icon="mdi:whatsapp" class="text-sm" />
              </button>

              <button
                @click="generarPDFRecepcion(item)"
                title="PDF Formato Carta"
                class="p-1.5 bg-red-50 dark:bg-red-900/30 text-red-600 dark:text-red-400 rounded-lg hover:bg-red-600 hover:text-white transition-all shadow-sm"
              >
                <Icon icon="mdi:file-pdf-box" class="text-sm" />
              </button>

              <button
                v-if="item.nombreEstado?.toLowerCase() !== 'entregado'"
                @click="abrirEditarModal(item)"
                title="Editar"
                class="p-1.5 bg-violet-50 dark:bg-violet-900/30 text-violet-600 dark:text-violet-400 rounded-lg hover:bg-violet-600 hover:text-white transition-all shadow-sm"
              >
                <Icon icon="mdi:pencil" class="text-sm" />
              </button>

              <button 
                @click="verDetallesRecepcion(item)"
                title="Ver Detalle"
                class="p-1.5 bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 rounded-lg hover:bg-blue-600 hover:text-white transition-all shadow-sm"
              >
                <Icon icon="mdi:eye" class="text-sm" />
              </button>
              
              <button 
                @click="imprimirTicketLocal(item)"
                title="Imprimir"
                class="p-1.5 bg-amber-50 dark:bg-amber-900/30 text-amber-600 dark:text-amber-400 rounded-lg hover:bg-amber-600 hover:text-white transition-all shadow-sm"
              >
                <Icon icon="mdi:printer" class="text-sm" />
              </button>
            </div>
            
            <div class="w-px h-6 bg-gray-200 dark:bg-gray-700 mx-0.5"></div>
            
            <div class="flex items-center gap-1 flex-wrap">
              <template v-if="item.nombreEstado?.toUpperCase() !== 'ENTREGADO'">
                <button 
                  @click="$router.push(`/seguimiento/${item.numeroOrden}?tab=2`)"
                  title="Reparar"
                  class="p-1.5 bg-indigo-50 dark:bg-indigo-900/30 text-indigo-600 dark:text-indigo-400 rounded-lg hover:bg-indigo-600 hover:text-white transition-all shadow-sm"
                >
                  <Icon icon="mdi:tools" class="text-sm" />
                </button>

                <!-- ✅ FIX: Botón Pagar / Registrar Pago Online Mobile — OCULTO si está "Por Diagnosticar" -->
                <button 
                  v-if="!esTotalmentePagado(item) && !esEstadoPorDiagnosticar(item) && !esSinCosto(item)"
                  @click="abrirModalPago(item)"
                  :title="tieneAbono(item) ? 'Completar Pago / Registrar Abono' : 'Registrar Pago / Pago Online'"
                  class="p-1.5 bg-emerald-50 dark:bg-emerald-900/30 text-emerald-600 dark:text-emerald-400 rounded-lg hover:bg-emerald-600 hover:text-white transition-all shadow-sm"
                >
                  <Icon icon="mdi:cash-plus" class="text-sm" />
                </button>

                <!-- ✅ Indicador de Pago Online Pendiente de Verificación -->
                <span 
                  v-if="esPagoTotalmentePendienteVerificar(item)"
                  title="Pago online pendiente de verificación en /pagos"
                  class="p-1.5 bg-amber-100 dark:bg-amber-950/60 text-amber-700 dark:text-amber-300 rounded-lg inline-flex items-center justify-center cursor-default"
                >
                  <Icon icon="mdi:clock-alert-outline" class="text-sm" />
                </span>

                <!-- ✅ Indicador Pagado 100% Verificado -->
                <span 
                  v-else-if="esTotalmentePagado(item) && !esSinCosto(item)"
                  title="Orden Pagada al 100%"
                  class="p-1.5 bg-emerald-100 dark:bg-emerald-950/60 text-emerald-600 dark:text-emerald-400 rounded-lg inline-flex items-center justify-center cursor-default"
                >
                  <Icon icon="mdi:check-decagram" class="text-sm" />
                </span>

                <!-- Botón Dar Salida: visible cuando está REPARADO, pagado al 100% y verificado -->
                <button
                  v-if="item.nombreEstado?.toLowerCase() === 'reparado' && esTotalmentePagado(item) && !esPagoTotalmentePendienteVerificar(item)"
                  @click="entregarSinPago(item)"
                  title="Dar Salida / Entregar Equipo"
                  class="p-1.5 bg-teal-50 dark:bg-teal-900/30 text-teal-600 dark:text-teal-400 rounded-lg hover:bg-teal-600 hover:text-white transition-all shadow-sm"
                >
                  <Icon icon="mdi:truck-fast" class="text-sm" />
                </button>

                <button 
                  v-if="item.nombreEstado?.toLowerCase().includes('imposible')"
                  @click="entregarSinPago(item)"
                  title="Entregar sin Cobro"
                  class="p-1.5 bg-orange-50 dark:bg-orange-900/30 text-orange-600 dark:text-orange-400 rounded-lg hover:bg-orange-600 hover:text-white transition-all shadow-sm"
                >
                  <Icon icon="mdi:truck-delivery" class="text-sm" />
                </button>

                <button 
                  v-if="item.nombreEstado?.toLowerCase().includes('garant')"
                  @click="finalizarGarantia(item)"
                  title="Finalizar Garantía"
                  class="p-1.5 bg-indigo-50 dark:bg-indigo-900/30 text-indigo-600 dark:text-indigo-400 rounded-lg hover:bg-indigo-600 hover:text-white transition-all shadow-sm"
                >
                  <Icon icon="mdi:check-circle-outline" class="text-sm" />
                </button>

                <button 
                  v-if="item.nombreEstado?.toLowerCase() === 'reparado' && !esTotalmentePagado(item)"
                  @click="$router.push(`/seguimiento/${item.numeroOrden}?tab=3`)"
                  title="Ir a Entrega y Cobro"
                  class="p-1.5 bg-teal-50 dark:bg-teal-900/30 text-teal-600 dark:text-teal-400 rounded-lg hover:bg-teal-600 hover:text-white transition-all shadow-sm"
                >
                  <Icon icon="mdi:truck-delivery-outline" class="text-sm" />
                </button>

                <button
                  v-if="esPorDiagnosticar(item)"
                  @click="abrirPagoExpress(item)"
                  title="Diagnóstico Express"
                  class="p-1.5 bg-yellow-50 dark:bg-yellow-900/30 text-yellow-600 dark:text-yellow-300 rounded-lg hover:bg-yellow-600 hover:text-white transition-all shadow-sm"
                >
                  <Icon icon="mdi:flash" class="text-sm" />
                </button>
              </template>
              
              <div v-else class="flex items-center gap-1">
                <div class="px-2 py-0.5 bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-400 rounded-lg border border-green-200 dark:border-green-800 text-[8px] font-black uppercase flex items-center gap-1">
                  <Icon icon="mdi:check-decagram" class="text-sm" />
                </div>
                <button 
                  @click="ponerEnGarantia(item)"
                  title="Pasar a Garantía"
                  class="p-1.5 bg-rose-50 dark:bg-rose-900/30 text-rose-600 dark:text-rose-400 rounded-lg hover:bg-rose-600 hover:text-white transition-all shadow-sm"
                >
                  <Icon icon="mdi:shield-alert" class="text-sm" />
                </button>
              </div>
            </div>
            
            <div v-if="esAdmin" class="w-px h-6 bg-gray-200 dark:bg-gray-700 mx-0.5"></div>
            
            <div v-if="esAdmin" class="flex items-center gap-1">
              <button
                @click="eliminarRegistro(item)"
                title="Eliminar"
                class="p-1.5 bg-red-50 dark:bg-red-900/30 text-red-600 dark:text-red-400 rounded-lg hover:bg-red-600 hover:text-white transition-all shadow-sm"
              >
                <Icon icon="mdi:trash-can-outline" class="text-sm" />
              </button>
            </div>
          </div>
        </div>
      </div>

      <!-- Pagination -->
      <div v-if="recepcionesFiltradas.length > 0" class="bg-gray-50/50 dark:bg-gray-800/50 px-3 sm:px-5 py-3 border-t border-gray-200 dark:border-gray-700 flex flex-col sm:flex-row items-center justify-between gap-3">
        <div class="text-[10px] sm:text-[11px] font-black text-gray-400 uppercase tracking-widest text-center sm:text-left">
          MOSTRANDO {{ (currentPage - 1) * itemsPerPage + 1 }} - {{ Math.min(currentPage * itemsPerPage, recepcionesFiltradas.length) }} DE {{ recepcionesFiltradas.length }}
        </div>
        <div class="flex items-center gap-1 flex-wrap justify-center">
          <button @click="goToFirstPage" :disabled="currentPage === 1" :class="['p-1.5 rounded-lg border transition-all hidden sm:inline-flex', currentPage === 1 ? 'opacity-30 cursor-not-allowed border-gray-200 dark:border-gray-700' : 'hover:bg-white dark:hover:bg-gray-700 border-gray-200 dark:border-gray-600 shadow-sm']">
            <Icon icon="mdi:chevron-double-left" class="text-base sm:text-lg" />
          </button>
          <button @click="currentPage = Math.max(currentPage - 1, 1)" :disabled="currentPage === 1" :class="['p-1.5 rounded-lg border transition-all', currentPage === 1 ? 'opacity-30 cursor-not-allowed border-gray-200 dark:border-gray-700' : 'hover:bg-white dark:hover:bg-gray-700 border-gray-200 dark:border-gray-600 shadow-sm']">
            <Icon icon="mdi:chevron-left" class="text-base sm:text-2xl" />
          </button>
          <div class="hidden sm:flex items-center gap-0.5 sm:gap-1 mx-1 sm:mx-2">
            <button v-for="page in displayedPages" :key="page" @click="currentPage = page" :class="['w-7 h-7 sm:w-9 sm:h-9 rounded-lg text-[10px] sm:text-xs font-black transition-all flex items-center justify-center', currentPage === page ? 'bg-indigo-600 text-white shadow-md shadow-indigo-500/30' : 'text-gray-500 hover:bg-white dark:hover:bg-gray-700 border border-transparent hover:border-gray-200 dark:hover:border-gray-600']">
              {{ page }}
            </button>
          </div>
          <span class="sm:hidden text-xs font-black text-gray-600 dark:text-gray-300 px-2">{{ currentPage }} / {{ totalPages }}</span>
          <button @click="currentPage = Math.min(currentPage + 1, totalPages)" :disabled="currentPage === totalPages" :class="['p-1.5 rounded-lg border transition-all', currentPage === totalPages ? 'opacity-30 cursor-not-allowed border-gray-200 dark:border-gray-700' : 'hover:bg-white dark:hover:bg-gray-700 border-gray-200 dark:border-gray-600 shadow-sm']">
            <Icon icon="mdi:chevron-right" class="text-base sm:text-2xl" />
          </button>
          <button @click="goToLastPage" :disabled="currentPage === totalPages" :class="['p-1.5 rounded-lg border transition-all hidden sm:inline-flex', currentPage === totalPages ? 'opacity-30 cursor-not-allowed border-gray-200 dark:border-gray-700' : 'hover:bg-white dark:hover:bg-gray-700 border-gray-200 dark:border-gray-600 shadow-sm']">
            <Icon icon="mdi:chevron-double-right" class="text-base sm:text-lg" />
          </button>
        </div>
      </div>
    </div>

    <!-- MODALES -->
    <Teleport to="body">
      <div v-if="showEditModal" class="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 z-[9998]" @click.self="showEditModal = false">
        <div class="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-full max-w-md overflow-hidden animate-zoomIn">
          <div class="bg-gradient-to-r from-violet-600 to-purple-600 px-4 sm:px-6 py-4 flex items-center gap-3">
            <div class="p-2 bg-white/20 rounded-xl">
              <Icon icon="mdi:pencil-box" class="text-white text-xl" />
            </div>
            <div>
              <h3 class="text-sm font-black text-white">Editar Recepción</h3>
              <p class="text-[10px] text-white/70 font-bold">#{{ editForm.numeroOrden }}</p>
            </div>
            <button @click="showEditModal = false" class="ml-auto p-1.5 bg-white/10 hover:bg-white/20 rounded-lg transition-all">
              <Icon icon="mdi:close" class="text-white text-lg" />
            </button>
          </div>
          <div class="p-4 sm:p-6 space-y-5">
            <div>
              <label class="block text-[10px] font-black text-gray-400 tracking-widest mb-2">
                <Icon icon="mdi:bag-personal" class="inline mr-1" /> Accesorios
              </label>
              <textarea
                v-model="editForm.accesorios"
                rows="3"
                placeholder="Ej: Cable, cargador, funda..."
                class="w-full px-3 sm:px-4 py-3 bg-gray-50 dark:bg-gray-900/50 border-2 border-gray-100 dark:border-gray-700 focus:border-violet-500 rounded-xl text-xs font-medium text-gray-800 dark:text-white outline-none transition-all resize-none"
                @blur="editForm.accesorios = toTitleCase(editForm.accesorios)"
              />
            </div>
            <div>
              <label class="block text-[10px] font-black text-gray-400 tracking-widest mb-2">
                <Icon icon="mdi:store-marker" class="inline mr-1" /> Sede / Taller
              </label>
              <select
                v-model="editForm.idSede"
                class="w-full px-3 sm:px-4 py-3 bg-gray-50 dark:bg-gray-900/50 border-2 border-gray-100 dark:border-gray-700 focus:border-violet-500 rounded-xl text-xs font-black text-gray-800 dark:text-white outline-none transition-all appearance-none cursor-pointer"
              >
                <option :value="null">— Sin sede asignada —</option>
                <option v-for="s in sedesList" :key="s.idSede" :value="s.idSede">{{ s.nombre }}</option>
              </select>
            </div>
          </div>
          <div class="px-4 sm:px-6 pb-6 flex flex-col sm:flex-row gap-3">
            <button @click="showEditModal = false" class="w-full sm:flex-1 py-3 border-2 border-gray-100 dark:border-gray-700 text-gray-600 dark:text-gray-400 rounded-xl font-black text-xs tracking-widest hover:bg-gray-50 dark:hover:bg-gray-700 transition-all">Cancelar</button>
            <button @click="guardarEdicion" :disabled="savingEdit" class="w-full sm:flex-1 py-3 bg-gradient-to-r from-violet-600 to-purple-600 text-white rounded-xl font-black text-xs uppercase tracking-widest hover:opacity-90 transition-all shadow-lg shadow-violet-500/30 disabled:opacity-50 flex items-center justify-center gap-2">
              <Icon :icon="savingEdit ? 'mdi:loading' : 'mdi:content-save'" :class="{'animate-spin': savingEdit}" />
              {{ savingEdit ? 'Guardando...' : 'Guardar Cambios' }}
            </button>
          </div>
        </div>
      </div>
    </Teleport>

    <Teleport to="body">
      <div v-if="showEntregaRapidaModal" class="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 z-[9999]">
        <div class="bg-white dark:bg-gray-800 rounded-[1.5rem] shadow-2xl w-full max-w-md overflow-hidden animate-zoomIn">
          <div class="bg-gradient-to-r from-orange-500 to-amber-600 px-4 sm:px-6 py-5 flex items-center gap-3">
            <div class="p-2 bg-white/20 rounded-xl">
              <Icon icon="mdi:truck-delivery" class="text-white text-2xl" />
            </div>
            <div>
              <h3 class="text-lg font-black text-white">Entrega sin Cobro</h3>
              <p class="text-[10px] text-white/70 font-bold uppercase tracking-widest">Orden #{{ entregaRapidaForm.item?.numeroOrden }}</p>
            </div>
            <button @click="showEntregaRapidaModal = false" class="ml-auto p-1.5 bg-white/10 hover:bg-white/20 rounded-lg transition-all">
              <Icon icon="mdi:close" class="text-white text-lg" />
            </button>
          </div>
          <div class="p-4 sm:p-7 space-y-6">
            <div class="p-4 bg-orange-50 dark:bg-orange-900/20 border border-orange-100 dark:border-orange-900/30 rounded-2xl">
              <p class="text-sm text-orange-800 dark:text-orange-300 font-medium leading-relaxed">
                <template v-if="esTotalmentePagado(entregaRapidaForm.item)">
                  El equipo está <b>pagado al 100%</b>. Esta acción registrará la entrega al cliente.
                </template>
                <template v-else>
                  Esta acción entregará el equipo <b>sin registrar ningún pago en caja</b>. Ideal para equipos imposibles de reparar o devoluciones.
                </template>
              </p>
            </div>
            <div>
              <label class="block text-[11px] font-black text-gray-400 tracking-widest mb-3 uppercase">Técnico que entrega</label>
              <div class="flex items-center gap-3 p-3 sm:p-4 bg-green-50 dark:bg-green-900/20 border-2 border-green-200 dark:border-green-800 rounded-2xl">
                <div class="p-2 bg-green-500 text-white rounded-xl">
                  <Icon icon="mdi:account-check" class="text-xl" />
                </div>
                <div>
                  <p class="font-black text-gray-800 dark:text-white text-sm">{{ obtenerNombreTecnicoLogueado() }}</p>
                  <p class="text-[9px] font-bold text-green-600 dark:text-green-400 uppercase tracking-widest">✓ Usuario logueado</p>
                </div>
              </div>
            </div>
            <div class="flex flex-col sm:flex-row gap-4">
              <button @click="showEntregaRapidaModal = false" class="w-full sm:flex-1 px-5 py-4 border-2 border-gray-100 dark:border-gray-700 text-gray-700 dark:text-gray-300 rounded-xl font-black text-xs uppercase tracking-widest hover:bg-gray-50 dark:hover:bg-gray-700 transition-all">Cancelar</button>
              <button @click="confirmarEntregaSinPago" :disabled="loading" class="w-full sm:flex-1 px-5 py-4 bg-gradient-to-r from-orange-500 to-amber-600 text-white rounded-xl font-black text-xs uppercase tracking-widest shadow-lg shadow-orange-500/30 disabled:opacity-50 disabled:shadow-none hover:opacity-90 transition-all flex items-center justify-center gap-2">
                <Icon v-if="loading" icon="mdi:loading" class="animate-spin text-lg" />
                Confirmar Entrega
              </button>
            </div>
          </div>
        </div>
      </div>
    </Teleport>

    <Teleport to="body">
      <div v-if="showPagoExpressModal" class="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 z-[10000]">
        <div class="bg-white dark:bg-gray-900 rounded-[1.5rem] shadow-2xl w-full max-w-lg overflow-hidden animate-zoomIn">
          <div class="bg-gradient-to-r from-yellow-500 to-amber-500 px-4 sm:px-6 py-5 flex items-center gap-3">
            <div class="p-2 bg-white/20 rounded-xl">
              <Icon icon="mdi:flash-circle" class="text-white text-2xl" />
            </div>
            <div>
              <h3 class="text-lg font-black text-white">Confirmar Diagnóstico Express</h3>
              <p class="text-[10px] text-white/80 font-bold uppercase tracking-widest">Orden #{{ pagoExpressItem?.numeroOrden || pagoExpressItem?.NumeroOrden }}</p>
            </div>
            <button @click="cancelarPagoExpress" class="ml-auto p-1.5 bg-white/10 hover:bg-white/20 rounded-lg transition-all">
              <Icon icon="mdi:close" class="text-white text-lg" />
            </button>
          </div>
          <div class="p-4 sm:p-7 space-y-6">
            <div class="rounded-3xl border border-yellow-100 dark:border-yellow-900/40 bg-yellow-50 dark:bg-yellow-900/20 p-5">
              <p class="text-sm font-black text-yellow-700 dark:text-yellow-200 leading-relaxed">
                Esta acción convertirá la recepción a <span class="uppercase">Diagnóstico Express</span> y registrará un pago de <b>$10 USD</b>.
              </p>
            </div>
            <div class="space-y-4">
              <div>
                <label class="block text-[11px] font-black text-gray-400 tracking-widest mb-3 uppercase">Método de Pago</label>
                <div class="grid grid-cols-2 gap-2">
                  <button v-for="metodo in metodosPago" :key="metodo.value" type="button" @click="pagoExpress.metodoPago = metodo.value" :class="['py-2.5 sm:py-3 px-3 sm:px-4 rounded-2xl border transition-all text-left text-[10px] sm:text-xs font-black', pagoExpress.metodoPago === metodo.value ? 'border-yellow-500 bg-yellow-100 text-yellow-900 shadow-sm' : 'border-gray-200 bg-white dark:bg-gray-900 text-gray-700 dark:text-gray-200']">
                    <div class="flex items-center gap-2">
                      <Icon :icon="metodo.icon" class="text-sm sm:text-base" />
                      <span class="text-[10px] sm:text-xs">{{ metodo.label }}</span>
                    </div>
                  </button>
                </div>
              </div>
              <div v-if="pagoExpress.metodoPago && pagoExpress.metodoPago !== 'EFECTIVO_USD'">
                <label class="block text-[11px] font-black text-gray-400 tracking-widest mb-3 uppercase">Referencia de Pago</label>
                <input v-model="pagoExpress.referencia" type="text" placeholder="Ingresa referencia, número de transferencia, o datos de pago" class="w-full px-3 sm:px-4 py-3 bg-gray-50 dark:bg-gray-900/50 border-2 border-gray-100 dark:border-gray-700 focus:border-yellow-500 rounded-2xl text-xs font-black text-gray-900 dark:text-white outline-none transition-all" />
              </div>
            </div>
            <div class="space-y-3 text-sm text-gray-600 dark:text-gray-300">
              <p><span class="font-black">Cliente:</span> {{ pagoExpressItem?.nombreCliente || pagoExpressItem?.NombreCliente || 'N/A' }}</p>
              <p><span class="font-black">Problema:</span> {{ pagoExpressItem?.problema || pagoExpressItem?.Problema || 'N/A' }}</p>
            </div>
            <div class="flex flex-col sm:flex-row gap-3">
              <button type="button" @click="cancelarPagoExpress" class="w-full sm:flex-1 px-5 py-3 border-2 border-gray-200 dark:border-gray-700 rounded-2xl font-black text-xs uppercase tracking-widest text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-800 transition-all">Cancelar</button>
              <button type="button" @click="confirmarPagoExpress" :disabled="procesandoPagoExpress || !pagoExpress.metodoPago" class="w-full sm:flex-1 px-5 py-3 bg-gradient-to-r from-yellow-500 to-amber-600 text-white rounded-2xl font-black text-xs uppercase tracking-widest hover:opacity-90 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2">
                <Icon :icon="procesandoPagoExpress ? 'mdi:loading' : 'mdi:check-bold'" :class="{'animate-spin': procesandoPagoExpress}" class="text-base" />
                {{ procesandoPagoExpress ? 'Procesando...' : 'Confirmar Pago' }}
              </button>
            </div>
          </div>
        </div>
      </div>
    </Teleport>

    <Teleport to="body">
      <div v-if="showConfirmModal" class="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 z-[10000]">
        <div class="bg-white dark:bg-gray-800 rounded-[2rem] shadow-2xl w-full max-w-sm overflow-hidden animate-zoomIn border border-white/20">
          <div class="p-6 sm:p-8 text-center">
            <div :class="[confirmModalIconClass, 'w-16 h-16 sm:w-20 sm:h-20 rounded-full flex items-center justify-center mx-auto mb-6 shadow-lg shadow-black/5']">
              <Icon :icon="confirmModalIcon" :class="[confirmModalIconColor, 'text-3xl sm:text-4xl']" />
            </div>
            <h3 class="text-lg sm:text-xl font-black text-gray-900 dark:text-white mb-2 leading-tight">{{ confirmModalTitle }}</h3>
            <p class="text-sm text-gray-500 dark:text-gray-400 font-medium leading-relaxed mb-8" v-html="confirmModalMessage"></p>
            <div class="flex flex-col gap-3">
              <button @click="aceptarConfirmacion" :class="[confirmModalConfirmClass, 'w-full py-4 text-white rounded-2xl font-black text-xs uppercase tracking-[0.2em] shadow-xl transition-all active:scale-95']">{{ confirmModalConfirmText }}</button>
              <button @click="cancelarConfirmacion" class="w-full py-4 bg-gray-50 dark:bg-gray-700/50 text-gray-400 dark:text-gray-500 rounded-2xl font-black text-xs uppercase tracking-[0.2em] hover:bg-gray-100 dark:hover:bg-gray-700 transition-all">{{ confirmModalCancelText }}</button>
            </div>
          </div>
        </div>
      </div>
    </Teleport>

    <Teleport to="body">
      <div v-if="showDetallesModal" class="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center p-2 sm:p-4 z-[9997]" @click.self="showDetallesModal = false">
        <div class="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-full max-w-4xl flex flex-col overflow-hidden animate-zoomIn max-h-[95vh]">
          <div class="bg-gradient-to-r from-blue-600 to-indigo-600 px-4 sm:px-6 py-3 sm:py-4 flex items-center justify-between flex-shrink-0">
            <div class="flex items-center gap-2 sm:gap-3">
              <div class="p-1.5 sm:p-2 bg-white/20 rounded-xl">
                <Icon icon="mdi:receipt-text" class="text-white text-lg sm:text-xl" />
              </div>
              <div>
                <h3 class="text-sm sm:text-base font-bold text-white uppercase tracking-wider">
                  Orden #{{ recepcionSeleccionada?.numeroOrden }}
                </h3>
                <p class="text-[8px] sm:text-[10px] text-blue-100 font-bold">
                  {{ formatearFecha(recepcionSeleccionada?.fechaIngreso) }} - {{ recepcionSeleccionada?.nombreCliente }}
                </p>
              </div>
            </div>
            <button @click="showDetallesModal = false" class="p-1.5 bg-white/10 hover:bg-white/20 rounded-lg transition-all">
              <Icon icon="mdi:close" class="text-white text-lg sm:text-xl" />
            </button>
          </div>

          <div class="flex bg-gray-50 dark:bg-gray-800/50 border-b border-gray-200 dark:border-gray-700 p-1 flex-shrink-0">
            <button 
              v-for="tab in ['resumen', 'equipo', 'servicios', 'pagos', 'bitacora']" 
              :key="tab" 
              @click="tabActiva = tab" 
              :class="['flex-1 py-1.5 sm:py-2 text-[8px] sm:text-[10px] font-black uppercase rounded-lg transition-all', 
              tabActiva === tab ? 'bg-white dark:bg-gray-700 text-blue-600 shadow-sm' : 'text-gray-400 hover:text-gray-600']"
            >
              {{ tab }}
            </button>
          </div>

          <div class="flex-1 overflow-y-auto p-3 sm:p-6 custom-scrollbar">
            <div v-if="tabActiva === 'resumen'" class="space-y-3 sm:space-y-4 animate-fadeIn">
              <div class="grid grid-cols-1 sm:grid-cols-2 gap-2 sm:gap-4">
                <div class="p-3 sm:p-4 bg-gray-50 dark:bg-gray-800/50 rounded-2xl border border-gray-100 dark:border-gray-700">
                  <span class="text-[9px] sm:text-[10px] font-bold text-gray-400 uppercase block mb-0.5 sm:mb-1">Cliente</span>
                  <span class="text-sm sm:text-base font-black text-gray-800 dark:text-white">{{ recepcionSeleccionada?.nombreCliente || 'N/A' }}</span>
                  <p class="text-xs text-gray-500">{{ recepcionSeleccionada?.documentoCliente }}</p>
                </div>
                <div class="p-3 sm:p-4 bg-gray-50 dark:bg-gray-800/50 rounded-2xl border border-gray-100 dark:border-gray-700">
                  <span class="text-[9px] sm:text-[10px] font-bold text-gray-400 uppercase block mb-0.5 sm:mb-1">Estado Actual</span>
                  <div :class="getEstadoStyle(recepcionSeleccionada?.nombreEstado)" class="px-2 py-1 rounded-lg text-xs font-black uppercase inline-block">
                    {{ recepcionSeleccionada?.nombreEstado }}
                  </div>
                </div>
              </div>
              
              <div class="grid grid-cols-2 sm:grid-cols-4 gap-2">
                <div class="p-2 sm:p-3 bg-blue-500/5 rounded-xl text-center">
                  <p class="text-[8px] sm:text-[9px] font-bold text-blue-400 uppercase mb-0.5">Total USD</p>
                  <p class="text-xs sm:text-sm font-black text-blue-600">${{ (recepcionSeleccionada?.precioTotalUsd || 0).toFixed(2) }}</p>
                </div>
                <div class="p-2 sm:p-3 bg-purple-500/5 rounded-xl text-center">
                  <p class="text-[8px] sm:text-[9px] font-bold text-purple-400 uppercase mb-0.5">Sede</p>
                  <p class="text-[8px] sm:text-[10px] font-black text-purple-600 uppercase truncate">{{ recepcionSeleccionada?.nombreSede || 'Central' }}</p>
                </div>
                <div class="p-2 sm:p-3 bg-amber-500/5 rounded-xl text-center col-span-2">
                  <p class="text-[8px] sm:text-[9px] font-bold text-amber-400 uppercase mb-0.5">Técnicos</p>
                  <p class="text-[8px] sm:text-[10px] font-black text-amber-600 uppercase truncate">
                    R: {{ recepcionSeleccionada?.nombreTecnicoRecepcion || '-' }} | 
                    Rep: {{ recepcionSeleccionada?.nombreTecnicoReparador || '-' }}
                  </p>
                </div>
              </div>
            </div>

            <div v-if="tabActiva === 'equipo'" class="space-y-3 animate-fadeIn">
              <div class="p-4 bg-gray-50 dark:bg-gray-800/50 rounded-xl border border-gray-100 dark:border-gray-700">
                <span class="text-[10px] font-black text-gray-400 uppercase block mb-1">Equipo / Descripción</span>
                <p class="text-sm font-bold text-gray-800 dark:text-white">{{ recepcionSeleccionada?.descripcion }}</p>
              </div>
              <div class="p-4 bg-red-50 dark:bg-red-900/10 rounded-xl border border-red-100 dark:border-red-900/30">
                <span class="text-[10px] font-black text-red-400 uppercase block mb-1">Problema Reportado</span>
                <p class="text-sm text-red-800 dark:text-red-300">{{ recepcionSeleccionada?.problema }}</p>
              </div>
              <div class="p-4 bg-gray-50 dark:bg-gray-800/50 rounded-xl border border-gray-100 dark:border-gray-700">
                <span class="text-[10px] font-black text-gray-400 uppercase block mb-1">Accesorios</span>
                <p class="text-sm text-gray-600 dark:text-gray-400">{{ recepcionSeleccionada?.accesorios || 'Ninguno' }}</p>
              </div>
            </div>

            <div v-if="tabActiva === 'servicios'" class="space-y-3 animate-fadeIn">
              <div class="bg-white dark:bg-gray-800 rounded-xl shadow border border-gray-100 dark:border-gray-700 overflow-hidden">
                <div class="bg-gradient-to-r from-green-50 to-emerald-50 dark:from-gray-800 dark:to-gray-800 px-4 py-3 border-b border-gray-100 dark:border-gray-700">
                  <h2 class="text-sm font-bold text-gray-800 dark:text-white flex items-center">
                    <div class="p-1.5 bg-green-100 dark:bg-green-900/30 rounded-lg mr-2">
                      <Icon icon="mdi:cash-multiple" class="text-green-600 dark:text-green-400 text-lg" />
                    </div>
                    Servicios y Productos
                  </h2>
                </div>
                <div class="p-4">
                  <div v-if="itemsDetalle.length === 0" class="py-6 text-center">
                    <Icon icon="mdi:package-variant-remove" class="text-4xl text-gray-300 mx-auto mb-2" />
                    <p class="text-sm text-gray-400">No hay servicios ni productos registrados</p>
                  </div>

                  <div v-if="itemsDetalle.length > 0" class="space-y-2">
                    <div v-for="it in itemsDetalle" :key="it.uuid" class="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-900/50 rounded-xl border border-gray-100 dark:border-gray-700">
                      <div class="flex items-center gap-3 flex-1 min-w-0">
                        <div :class="it.tipo === 'servicio' ? 'bg-green-100 dark:bg-green-900/30 text-green-600' : (it.tipo === 'producto' ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-600' : 'bg-orange-100 dark:bg-orange-900/30 text-orange-600')" class="p-2 rounded-lg shrink-0">
                          <Icon :icon="it.tipo === 'servicio' ? 'mdi:wrench' : (it.tipo === 'producto' ? 'mdi:package-variant' : 'mdi:tools')" class="text-sm" />
                        </div>
                        <div class="flex-1 min-w-0">
                          <div class="flex items-center gap-2 flex-wrap">
                            <p class="text-sm font-bold text-gray-800 dark:text-white truncate">{{ it.nombre }}</p>
                            <span v-if="it.cantidad && it.cantidad > 1" class="text-[9px] font-black bg-gray-200 dark:bg-gray-700 px-1.5 py-0.5 rounded-full">x{{ it.cantidad }}</span>
                          </div>
                          <p class="text-[9px] text-gray-400 font-black uppercase tracking-widest">{{ it.tipo }}</p>
                        </div>
                      </div>
                      <span class="text-sm font-black text-green-600 dark:text-green-400 ml-4">${{ (it.precio_total || 0).toFixed(2) }}</span>
                    </div>
                  </div>

                  <div v-if="itemsDetalle.length > 0" class="mt-4 pt-4 border-t border-gray-100 dark:border-gray-700">
                    <div class="flex justify-between items-center bg-green-50 dark:bg-green-900/20 px-4 py-3 rounded-xl border border-green-100 dark:border-green-800/40">
                      <span class="text-xs font-black text-green-700 dark:text-green-400 uppercase tracking-wider">TOTAL GENERAL:</span>
                      <span class="text-lg font-black text-green-600 dark:text-green-400">${{ totalGeneralDetalle.toFixed(2) }}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div v-if="tabActiva === 'pagos'" class="space-y-3 animate-fadeIn">
              <div v-if="pagosDetalle.length === 0" class="text-center py-8">
                <Icon icon="mdi:cash-off" class="text-4xl text-gray-300 mx-auto mb-2" />
                <p class="text-sm text-gray-400 font-bold uppercase">No se registran pagos</p>
              </div>

              <div v-else class="space-y-3">
                <div v-for="(pago, i) in pagosDetalle" :key="i" class="p-3 sm:p-4 rounded-xl bg-white dark:bg-gray-800 border border-gray-100 dark:border-gray-700 shadow-sm">
                  <div class="flex justify-between items-center border-b border-gray-50 dark:border-gray-700 pb-2 mb-2">
                    <span class="text-[10px] font-black text-indigo-500 uppercase">Recibo #{{ i+1 }}</span>
                    <span class="text-[10px] font-bold text-gray-400">{{ formatearFecha(pago.fechaPago || pago.fechaRegistro) }}</span>
                  </div>
                  <div class="flex justify-between items-end">
                    <div class="min-w-0 flex-1 pr-2">
                      <p class="text-xs font-bold text-gray-400 uppercase truncate">Vía: {{ pago.metodoPago || pago.tipoPago || 'Efectivo' }}</p>
                      <p v-if="pago.referencia" class="text-[10px] text-gray-400 truncate">Ref: {{ pago.referencia }}</p>
                    </div>
                    <div class="text-right shrink-0">
                      <p class="text-base font-black text-green-600">${{ (pago.montoUsd || 0).toFixed(2) }}</p>
                    </div>
                  </div>
                </div>

                <div class="mt-3 p-3 rounded-xl bg-gradient-to-r from-green-50 to-emerald-50 dark:from-green-900/20 dark:to-emerald-900/20 border border-green-100 dark:border-green-800/40 flex justify-between items-center">
                  <span class="text-xs font-black text-green-700 dark:text-green-400 uppercase tracking-wider">TOTAL COBRADO:</span>
                  <span class="text-lg font-black text-green-600 dark:text-green-400">${{ totalPagadoDetalle.toFixed(2) }}</span>
                </div>
              </div>
            </div>

            <div v-if="tabActiva === 'bitacora'" class="p-3 sm:p-4 bg-gray-50 dark:bg-gray-800/50 rounded-2xl min-h-[100px] border border-gray-100 dark:border-gray-700 animate-fadeIn">
              <span class="text-[10px] font-black text-gray-400 uppercase block mb-2">Actividades Realizadas</span>
              <p class="text-xs sm:text-sm text-gray-600 dark:text-gray-300 whitespace-pre-wrap">{{ recepcionSeleccionada?.actividadesRealizadas || 'Sin registros de bitácora.' }}</p>
            </div>
          </div>

          <div class="px-4 sm:px-6 py-3 sm:py-4 border-t border-gray-100 dark:border-gray-700 bg-gray-50/50 dark:bg-gray-800/50 flex flex-col sm:flex-row justify-end gap-2 flex-shrink-0">
            <button @click="showDetallesModal = false" class="w-full sm:w-auto px-4 sm:px-6 py-2 text-[10px] sm:text-xs font-black text-gray-400 uppercase hover:text-gray-600 transition-colors">Cerrar Detalle</button>
            <button 
              @click="$router.push(`/seguimiento/${recepcionSeleccionada?.numeroOrden}?tab=1`); showDetallesModal = false" 
              class="w-full sm:w-auto px-6 sm:px-8 py-2 bg-blue-600 text-white rounded-xl text-[10px] sm:text-xs font-black uppercase shadow-md hover:bg-blue-700 transition-all"
            >
              Ir a Gestión Completa
            </button>
          </div>
        </div>
      </div>
    </Teleport>

    <!-- ============================================ -->
    <!-- MODAL REGISTRO DE PAGO ONLINE / ADELANTADO -->
    <!-- ============================================ -->
    <Teleport to="body">
      <div v-if="showPagoModal" class="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center p-3 sm:p-4 z-[9999]" @click.self="cerrarModalPago">
        <div class="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden animate-zoomIn border border-gray-100 dark:border-gray-700 max-h-[95vh] flex flex-col">
          <!-- Header Modal -->
          <div class="bg-gradient-to-r from-emerald-600 via-teal-600 to-indigo-600 px-5 py-4 flex items-center justify-between text-white flex-shrink-0">
            <div class="flex items-center gap-3">
              <div class="p-2 bg-white/20 rounded-xl backdrop-blur-sm">
                <Icon icon="mdi:cash-fast" class="text-2xl" />
              </div>
              <div>
                <h3 class="text-base font-black uppercase tracking-wider flex items-center gap-2">
                  Registrar Pago Online / Adelantado
                </h3>
                <p class="text-[11px] text-emerald-100 font-bold">
                  Orden #{{ pagoModalItem?.numeroOrden }} · {{ pagoModalItem?.nombreCliente }}
                </p>
              </div>
            </div>
            <button @click="cerrarModalPago" class="p-1.5 bg-white/10 hover:bg-white/20 rounded-lg transition-all text-white">
              <Icon icon="mdi:close" class="text-xl" />
            </button>
          </div>

          <!-- Body Modal -->
          <div class="p-5 overflow-y-auto space-y-4 custom-scrollbar">
            <!-- Card de Resumen de la Orden -->
            <div class="bg-gradient-to-br from-gray-50 to-emerald-50/30 dark:from-gray-900/50 dark:to-emerald-950/20 rounded-xl p-3.5 border border-emerald-100 dark:border-emerald-800/40">
              <div class="flex justify-between items-start mb-2">
                <div>
                  <span class="text-[10px] font-black text-gray-400 uppercase tracking-widest block">Equipo / Servicio</span>
                  <p class="font-black text-gray-900 dark:text-white text-xs">{{ pagoModalItem?.descripcion || pagoModalItem?.nombreProductoServicio || 'Equipo' }}</p>
                  <p class="text-[11px] text-gray-500 italic">{{ pagoModalItem?.problema }}</p>
                </div>
                <div class="text-right">
                  <span class="text-[10px] font-black text-gray-400 uppercase tracking-widest block">Estado Actual</span>
                  <span :class="getEstadoStyle(pagoModalItem?.nombreEstado)" class="px-2 py-0.5 rounded-lg text-[9px] font-black uppercase inline-block">
                    {{ pagoModalItem?.nombreEstado }}
                  </span>
                </div>
              </div>

              <!-- Estadísticas de saldo -->
              <div class="grid grid-cols-3 gap-2 pt-2 border-t border-gray-200 dark:border-gray-700 text-center">
                <div class="p-2 bg-white dark:bg-gray-800 rounded-lg shadow-xs">
                  <span class="text-[9px] font-black text-gray-400 uppercase block">Total</span>
                  <span class="text-xs font-black text-gray-800 dark:text-gray-200">${{ obtenerTotalNeto(pagoModalItem).toFixed(2) }}</span>
                </div>
                <div class="p-2 bg-white dark:bg-gray-800 rounded-lg shadow-xs">
                  <span class="text-[9px] font-black text-emerald-600 block">Ya Pagado</span>
                  <span class="text-xs font-black text-emerald-600">${{ obtenerMontoPagado(pagoModalItem).toFixed(2) }}</span>
                </div>
                <div class="p-2 bg-white dark:bg-gray-800 rounded-lg shadow-xs border border-rose-200 dark:border-rose-900/50">
                  <span class="text-[9px] font-black text-rose-600 block">Resta / Saldo</span>
                  <span class="text-xs font-black text-rose-600">${{ obtenerSaldoRestante(pagoModalItem).toFixed(2) }}</span>
                </div>
              </div>
            </div>

            <!-- Selección de Monto -->
            <div>
              <div class="flex justify-between items-center mb-1.5">
                <label class="text-[11px] font-black text-gray-700 dark:text-gray-300 uppercase tracking-wider">
                  Monto a Pagar (USD) *
                </label>
                <div class="flex items-center gap-1">
                  <button 
                    type="button" 
                    @click="pagoForm.montoUsd = obtenerSaldoRestante(pagoModalItem)" 
                    class="px-2 py-0.5 text-[9px] font-black uppercase bg-emerald-100 dark:bg-emerald-950/60 text-emerald-700 dark:text-emerald-300 rounded hover:bg-emerald-200 transition-colors"
                  >
                    Saldo Total (${{ obtenerSaldoRestante(pagoModalItem).toFixed(2) }})
                  </button>
                  <button 
                    type="button" 
                    v-if="obtenerSaldoRestante(pagoModalItem) > 1"
                    @click="pagoForm.montoUsd = +(obtenerSaldoRestante(pagoModalItem) / 2).toFixed(2)" 
                    class="px-2 py-0.5 text-[9px] font-black uppercase bg-blue-100 dark:bg-blue-950/60 text-blue-700 dark:text-blue-300 rounded hover:bg-blue-200 transition-colors"
                  >
                    50%
                  </button>
                </div>
              </div>

              <div class="relative">
                <span class="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 font-black text-base">$</span>
                <input 
                  v-model.number="pagoForm.montoUsd" 
                  type="number" 
                  step="0.01" 
                  min="0.01" 
                  placeholder="0.00" 
                  class="w-full pl-8 pr-4 py-2.5 bg-gray-50 dark:bg-gray-900/50 border-2 border-gray-200 dark:border-gray-700 focus:border-emerald-500 rounded-xl font-black text-base text-gray-900 dark:text-white outline-none transition-all"
                />
              </div>

              <!-- Equivalente en Bolívares con tasa BCV -->
              <div class="mt-1.5 flex items-center justify-between px-2 text-[11px] text-gray-500 dark:text-gray-400">
                <span>Tasa Oficial: <b>{{ (tasaActiva?.PrecioUsdBs || tasaActiva?.precioUsdBs || tasaActiva?.precio_usd_bs || 1).toFixed(2) }} Bs/$</b></span>
                <span class="font-black text-emerald-600 dark:text-emerald-400">
                  ≈ Bs. {{ ((pagoForm.montoUsd || 0) * (tasaActiva?.PrecioUsdBs || tasaActiva?.precioUsdBs || tasaActiva?.precio_usd_bs || 1)).toFixed(2) }}
                </span>
              </div>
            </div>

            <!-- Método de Pago -->
            <div>
              <label class="block text-[11px] font-black text-gray-700 dark:text-gray-300 uppercase tracking-wider mb-1.5">
                Método de Pago *
              </label>
              <div class="grid grid-cols-2 sm:grid-cols-3 gap-2">
                <button
                  type="button"
                  v-for="metodo in metodosPago"
                  :key="metodo.value"
                  @click="pagoForm.metodoPago = metodo.value"
                  :class="[
                    'p-2.5 rounded-xl border text-left transition-all flex items-center gap-2',
                    pagoForm.metodoPago === metodo.value
                      ? 'border-emerald-500 bg-emerald-50/50 dark:bg-emerald-950/40 text-emerald-700 dark:text-emerald-300 ring-2 ring-emerald-500/20 shadow-xs'
                      : 'border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700/50 text-gray-700 dark:text-gray-300'
                  ]"
                >
                  <Icon :icon="metodo.icon" class="text-lg flex-shrink-0" :class="pagoForm.metodoPago === metodo.value ? 'text-emerald-600 dark:text-emerald-400' : 'text-gray-400'" />
                  <span class="text-xs font-black truncate">{{ metodo.label }}</span>
                </button>
              </div>
            </div>

            <!-- ✅ Cuenta Bancaria Destino (PRIMERO) -->
            <div v-if="metodoRequiereCuentaBancaria">
              <label class="block text-[11px] font-black text-gray-700 dark:text-gray-300 uppercase tracking-wider mb-1.5">
                Cuenta Bancaria Destino *
              </label>
              <select
                v-model="pagoForm.idCuentaBancaria"
                :disabled="cargandoCuentas"
                class="w-full px-3 py-2.5 bg-gray-50 dark:bg-gray-900/50 border-2 border-gray-200 dark:border-gray-700 focus:border-emerald-500 rounded-xl font-bold text-xs text-gray-900 dark:text-white outline-none transition-all disabled:opacity-60"
              >
                <option :value="null">
                  {{ cargandoCuentas ? 'Cargando cuentas...' : '— Selecciona cuenta destino —' }}
                </option>
                <option
                  v-for="c in cuentasPropias"
                  :key="c.idCuentaBancaria || c.IdCuentaBancaria"
                  :value="c.idCuentaBancaria || c.IdCuentaBancaria"
                >
                  {{ c.bancoNombre || c.banco?.nombre || 'Banco' }} ·
                  {{ formatearNumeroCuenta(c.numeroCuenta || c.NumeroCuenta) }} ·
                  {{ c.moneda || 'USD' }}
                  <template v-if="c.titularNombreCompleto || c.titular"> — {{ c.titularNombreCompleto || c.titular }}</template>
                </option>
              </select>
              <p v-if="cuentasPropias.length === 0 && !cargandoCuentas" class="mt-1 text-[10px] text-amber-600 dark:text-amber-400 font-bold">
                ⚠ No hay cuentas configuradas. Configúralas en el módulo de Bancos.
              </p>

              <!-- ✅ Tarjeta visual de la cuenta destino seleccionada (igual que en Ventas) -->
              <div
                v-if="cuentaDestinoSeleccionada"
                class="mt-3 p-4 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-2xl shadow-xl border border-blue-300/30"
              >
                <!-- Titular -->
                <div class="flex items-center gap-3 mb-3 pb-3 border-b border-white/20">
                  <div class="p-2.5 bg-white/20 rounded-xl shrink-0">
                    <Icon icon="mdi:account-tie" class="text-white text-3xl" />
                  </div>
                  <div class="min-w-0 flex-1">
                    <p class="text-[10px] font-black text-blue-100 uppercase tracking-widest mb-0.5">Titular</p>
                    <p class="text-white font-black text-lg leading-tight truncate">
                      {{ cuentaDestinoSeleccionada.titularNombreCompleto }}
                    </p>
                    <p class="text-blue-100 font-bold text-sm font-mono">
                      {{ cuentaDestinoSeleccionada.titularTipoDocumento === 'rif' ? 'RIF' : 'C.I.' }}:
                      {{ cuentaDestinoSeleccionada.titularDocumento }}
                    </p>
                  </div>
                </div>

                <!-- Teléfono -->
                <div class="flex items-center gap-3 mb-3 pb-3 border-b border-white/20">
                  <div class="p-2 bg-white/20 rounded-lg shrink-0">
                    <Icon icon="mdi:phone" class="text-white text-xl" />
                  </div>
                  <div class="flex-1">
                    <p class="text-[10px] font-black text-blue-100 uppercase tracking-widest mb-0.5">Teléfono</p>
                    <p class="text-white font-black text-lg font-mono tracking-wide">
                      {{ cuentaDestinoSeleccionada.titularTelefono || 'No registrado' }}
                    </p>
                  </div>
                </div>

                <!-- Datos Pago Móvil de la cuenta -->
                <div
                  v-if="cuentaDestinoSeleccionada.pmBancoCodigo || cuentaDestinoSeleccionada.pmTelefono"
                  class="flex items-center gap-3 bg-white/15 rounded-xl p-3 border border-white/20 mb-3"
                >
                  <div class="p-2 bg-white/25 rounded-lg shrink-0">
                    <Icon icon="mdi:cellphone" class="text-white text-xl" />
                  </div>
                  <div class="flex-1 min-w-0">
                    <p class="text-[10px] font-black text-blue-100 uppercase tracking-widest mb-0.5">Pago Móvil</p>
                    <p class="text-white font-black text-base font-mono tracking-wide truncate">
                      {{ cuentaDestinoSeleccionada.pmBancoCodigo || cuentaDestinoSeleccionada.bancoCodigo }}
                      · {{ cuentaDestinoSeleccionada.pmTelefono || cuentaDestinoSeleccionada.titularTelefono }}
                    </p>
                  </div>
                </div>

                <!-- N° Cuenta -->
                <div
                  v-if="cuentaDestinoSeleccionada.numeroCuenta"
                  class="flex items-center justify-between gap-2 text-[10px] font-mono text-blue-100 bg-black/20 rounded-lg px-3 py-2 mb-2"
                >
                  <span class="font-black uppercase tracking-widest">N° Cuenta:</span>
                  <span class="font-bold truncate">
                    {{ formatearNumeroCuenta(cuentaDestinoSeleccionada.numeroCuenta) }}
                  </span>
                </div>

                <!-- Banco -->
                <div class="flex items-center gap-2 text-[10px] font-mono text-blue-100 bg-black/20 rounded-lg px-3 py-2">
                  <Icon icon="mdi:bank" class="text-blue-100 text-sm" />
                  <span class="font-black uppercase tracking-widest">Banco:</span>
                  <span class="font-bold truncate">
                    {{ cuentaDestinoSeleccionada.bancoNombre }}
                    <template v-if="cuentaDestinoSeleccionada.bancoCodigo">
                      ({{ cuentaDestinoSeleccionada.bancoCodigo }})
                    </template>
                  </span>
                </div>
              </div>
            </div>

            <!-- ✅ Banco del Cliente (Pago Móvil) — SEGUNDO -->
            <div v-if="esPagoMovil">
              <label class="block text-[11px] font-black text-gray-700 dark:text-gray-300 uppercase tracking-wider mb-1.5">
                Banco del Cliente (Pago Móvil) *
              </label>
              <select
                v-model="pagoForm.idBanco"
                class="w-full px-3 py-2.5 bg-gray-50 dark:bg-gray-900/50 border-2 border-gray-200 dark:border-gray-700 focus:border-emerald-500 rounded-xl font-bold text-xs text-gray-900 dark:text-white outline-none transition-all"
              >
                <option :value="null">— Selecciona banco del cliente —</option>
                <option v-for="b in bancosPagoMovil" :key="b.idBanco || b.IdBanco" :value="b.idBanco || b.IdBanco">
                  {{ b.codigo || b.Codigo }} — {{ b.nombre || b.Nombre }}
                </option>
              </select>
              <p class="mt-1 text-[10px] text-gray-500 dark:text-gray-400">
                Banco desde el cual el cliente realizó el Pago Móvil
              </p>
            </div>

            <!-- ✅ Datos del Pago Móvil (emisor) -->
            <div v-if="esPagoMovil" class="grid grid-cols-2 gap-2">
              <div>
                <label class="block text-[10px] font-black text-gray-500 uppercase mb-1">Teléfono Emisor</label>
                <input
                  v-model="pagoForm.telefonoPagoMovil"
                  type="text"
                  inputmode="numeric"
                  placeholder="04141234567"
                  class="w-full px-3 py-2 bg-gray-50 dark:bg-gray-900/50 border border-gray-200 dark:border-gray-700 focus:border-emerald-500 rounded-xl text-xs font-bold outline-none"
                />
              </div>
              <div>
                <label class="block text-[10px] font-black text-gray-500 uppercase mb-1">Cédula Emisor</label>
                <input
                  v-model="pagoForm.cedulaPagoMovil"
                  type="text"
                  inputmode="numeric"
                  placeholder="V-12345678"
                  class="w-full px-3 py-2 bg-gray-50 dark:bg-gray-900/50 border border-gray-200 dark:border-gray-700 focus:border-emerald-500 rounded-xl text-xs font-bold outline-none"
                />
              </div>
            </div>

            <!-- Referencia de Pago (clave para pagos online / transferencias / pago móvil) -->
            <div>
              <label class="block text-[11px] font-black text-gray-700 dark:text-gray-300 uppercase tracking-wider mb-1">
                Número de Referencia / Comprobante
                <span v-if="metodoRequiereReferencia" class="text-rose-500">* (Obligatorio Online)</span>
              </label>
              <div class="relative">
                <Icon icon="mdi:receipt-text-check-outline" class="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-lg" />
                <input 
                  v-model="pagoForm.referencia" 
                  type="text" 
                  placeholder="Ej: 123456 / Últimos 4 dígitos / Hash" 
                  class="w-full pl-10 pr-4 py-2.5 bg-gray-50 dark:bg-gray-900/50 border-2 border-gray-200 dark:border-gray-700 focus:border-emerald-500 rounded-xl font-bold text-xs text-gray-900 dark:text-white outline-none transition-all"
                />
              </div>
            </div>

            <!-- Observaciones / Nota -->
            <div>
              <label class="block text-[11px] font-black text-gray-700 dark:text-gray-300 uppercase tracking-wider mb-1">
                Nota u Observaciones del Pago
              </label>
              <input 
                v-model="pagoForm.observaciones" 
                type="text" 
                placeholder="Ej: Pago adelantado vía Pago Móvil por el cliente" 
                class="w-full px-3 py-2 bg-gray-50 dark:bg-gray-900/50 border border-gray-200 dark:border-gray-700 focus:border-emerald-500 rounded-xl text-xs text-gray-900 dark:text-white outline-none"
              />
            </div>
          </div>

          <!-- Footer Modal -->
          <div class="p-4 bg-gray-50 dark:bg-gray-900/50 border-t border-gray-100 dark:border-gray-700 flex flex-col sm:flex-row gap-2 justify-end flex-shrink-0">
            <button 
              type="button" 
              @click="cerrarModalPago" 
              :disabled="procesandoPago"
              class="w-full sm:w-auto px-5 py-2.5 text-xs font-black text-gray-500 uppercase tracking-wider hover:bg-gray-200 dark:hover:bg-gray-700 rounded-xl transition-all"
            >
              Cancelar
            </button>
            <button 
              type="button" 
              @click="confirmarRegistroPago" 
              :disabled="procesandoPago || !pagoForm.montoUsd || pagoForm.montoUsd <= 0"
              class="w-full sm:w-auto px-6 py-2.5 bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700 text-white rounded-xl text-xs font-black uppercase tracking-wider shadow-md transition-all flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Icon :icon="procesandoPago ? 'mdi:loading' : 'mdi:check-circle'" :class="{'animate-spin': procesandoPago}" class="text-lg" />
              {{ procesandoPago ? 'Registrando Pago...' : 'Confirmar y Guardar Pago' }}
            </button>
          </div>
        </div>
      </div>
    </Teleport>
  </div>
</template>

<script>
import { Icon } from "@iconify/vue";
import { useToast } from "vue-toastification";
import * as api from '@/api/recepcionEquipo.js';
import { obtenerServicios } from '@/api/servicios.js';
import { obtenerProductos } from '@/api/productos.js';
import { obtenerEstadosRecepcion } from '@/api/estadoRecepcion.js';
import { obtenerTasaVentaActual } from '@/api/tasaVenta.js';
import { obtenerTecnicos } from '@/api/tecnicos.js';
import { imprimirTicket } from '@/utils/ticketGenerator.js';
import { obtenerSedesActivas } from '@/api/sede.js';
import { obtenerClientePorId } from '@/api/clientes.js';
import { crearDetalleRecepcion } from '@/api/recepcionEquipo.js';
import { crearSalidaEquipo } from '@/api/salidaEquipoDetalle.js';
import { crearPagoSalidaEquipo } from '@/api/pagoSalidaEquipo.js';
import { toTitleCase } from '@/utils/stringUtils.js';
import { notificarRecepcion } from '@/utils/notificacionesHelper';
import { getCurrentUser } from "@/utils/permissionUtils";
import * as XLSX from 'xlsx';
import { descargarOrdenServicioPDF } from '@/utils/reporteOrdenServicioGenerator.js';
import { obtenerPagosPorRecepcion } from '@/api/pagoSalidaEquipo.js';
import { obtenerPagos } from '@/api/pagos.js';
import {
  obtenerCuentasPropias,
  obtenerBancosPagoMovil,
  crearPagoCuenta,
  formatearNumeroCuenta
} from '@/api/bancos.js';

export default {
  name: 'RecepcionEquipoList',
  components: { Icon },
  setup() {
    const toast = useToast();
    return { toast };
  },
  data() {
    return {
      recepciones: [],
      recepcionesBusqueda: null,
      loading: false,
      buscandoApi: false,
      busquedaAbortController: null,
      filtroNumeroOrden: '',
      filtroCedula: '',
      filtroNombre: '',
      filtroProducto: '',
      filtroEstado: 'Por Diagnosticar',
      estadosList: [],
      sedesList: [],
      catalogoServicios: [],
      catalogoProductos: [],
      tasaActiva: null,
      tecnicos: [],
      debounceTimer: null,
      currentPage: 1,
      itemsPerPage: 5,
      maxVisiblePages: 10,
      showEditModal: false,
      savingEdit: false,
      editItemRef: null,
      editForm: { idRecepcionEquipo: null, numeroOrden: '', accesorios: '', idSede: null },
      showEntregaRapidaModal: false,
      entregaRapidaForm: { idTecnico: null, item: null, nombreTecnico: '' },
      showConfirmModal: false,
      confirmModalTitle: '',
      confirmModalMessage: '',
      confirmModalIcon: 'mdi:check-circle',
      confirmModalIconClass: 'bg-green-100 dark:bg-green-900/30',
      showPagoExpressModal: false,
      procesandoPagoExpress: false,
      pagoExpressItem: null,
      pagoExpress: { metodoPago: '', referencia: '' },
      showPagoModal: false,
      procesandoPago: false,
      pagoModalItem: null,
      pagoForm: {
        montoUsd: 0,
        metodoPago: 'PAGO_MOVIL',
        referencia: '',
        observaciones: '',
        idCuentaBancaria: null,
        idBanco: null,
        telefonoPagoMovil: '',
        cedulaPagoMovil: ''
      },
      cuentasPropias: [],
      bancosPagoMovil: [],
      cargandoCuentas: false,
      metodosPago: [
        { value: 'EFECTIVO_USD', label: 'Efectivo USD', icon: 'mdi:cash-usd' },
        { value: 'TRANSFERENCIA', label: 'Transferencia', icon: 'mdi:bank-transfer' },
        { value: 'PAGO_MOVIL', label: 'Pago Móvil', icon: 'mdi:cellphone-nfc' },
        { value: 'ZELLE', label: 'Zelle', icon: 'mdi:send-circle' },
        { value: 'BINANCE', label: 'Binance/Cripto', icon: 'mdi:currency-btc' },
        { value: 'PUNTO_VENTA', label: 'Punto de Venta', icon: 'mdi:credit-card' }
      ],
      confirmModalIconColor: 'text-green-600 dark:text-green-400',
      confirmModalConfirmText: 'Aceptar',
      confirmModalCancelText: 'Cancelar',
      confirmModalType: 'success',
      confirmModalConfirmClass: 'bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700',
      confirmCallback: null,
      confirmCancelCallback: null,
      importando: false,
      showDetallesModal: false,
      recepcionSeleccionada: null,
      tabActiva: 'resumen',
      pagosDetalle: [],
      loadingPagos: false,

      // ✅ NUEVO: caché de pagos reales por orden
      pagosPorOrden: {},
      cargandoPagosPorOrden: {},
      pagosGenerales: []
    };
  },
  computed: {
    esAdmin() { return localStorage.getItem('rol') === 'Administrador'; },
    hayFiltrosActivos() { return !!(this.filtroNumeroOrden || this.filtroCedula || this.filtroNombre || this.filtroProducto); },
    usandoBusquedaAvanzada() { return this.hayFiltrosActivos; },

    metodoRequiereCuentaBancaria() {
      return ['PAGO_MOVIL', 'TRANSFERENCIA', 'ZELLE', 'BINANCE', 'PUNTO_VENTA']
        .includes(this.pagoForm.metodoPago);
    },
    metodoRequiereReferencia() {
      return this.metodoRequiereCuentaBancaria;
    },
    esPagoMovil() {
      return this.pagoForm.metodoPago === 'PAGO_MOVIL';
    },
    cuentaDestinoSeleccionada() {
      if (!this.pagoForm.idCuentaBancaria) return null;
      return this.cuentasPropias.find(
        c => (c.idCuentaBancaria || c.IdCuentaBancaria) === this.pagoForm.idCuentaBancaria
      ) || null;
    },

    recepcionesFiltradas() {
      if (this.hayFiltrosActivos) {
        return this.recepcionesBusqueda || [];
      }
      let result = this.recepciones || [];
      if (this.filtroEstado === 'TODOS' || !this.filtroEstado) return result;
      if (this.filtroEstado === 'EXPRESS') {
        return result.filter(r => ((r.tipoDiagnostico || r.TipoDiagnostico || '').toUpperCase()).includes('EXPRE'));
      }
      const estadoFiltro = this.filtroEstado.toLowerCase();
      return result.filter(r => {
        const estado = (r.nombreEstado || r.NombreEstado || '').toLowerCase();
        return estado === estadoFiltro || estado.replace(/[_\s]/g, '') === estadoFiltro.replace(/[_\s]/g, '');
      });
    },

    mostrarEstadoVacio() {
      if (this.loading) return false;
      if (this.buscandoApi) return false;
      if (this.recepcionesFiltradas.length > 0) return false;
      if (this.hayFiltrosActivos && this.recepcionesBusqueda === null) return false;
      return true;
    },

    totalPages() { return Math.ceil(this.recepcionesFiltradas.length / this.itemsPerPage); },
    paginatedRecepciones() {
      const start = (this.currentPage - 1) * this.itemsPerPage;
      return this.recepcionesFiltradas.slice(start, start + this.itemsPerPage);
    },
    displayedPages() {
      const total = this.totalPages;
      const max = this.maxVisiblePages;
      const current = this.currentPage;
      if (total <= max) return Array.from({ length: total }, (_, i) => i + 1);
      let start = Math.max(1, current - Math.floor(max / 2));
      let end = start + max - 1;
      if (end > total) { end = total; start = Math.max(1, end - max + 1); }
      return Array.from({ length: end - start + 1 }, (_, i) => start + i);
    },
    itemsDetalle() {
      if (!this.recepcionSeleccionada) return [];
      const detalles = this.recepcionSeleccionada.recepcionEquipoDetalles || this.recepcionSeleccionada.detalles || [];
      if (!Array.isArray(detalles) || detalles.length === 0) return [];
      return detalles.map(det => {
        const cantidad = det.cantidad || det.Cantidad || 1;
        const subtotal = parseFloat(det.subtotalUsd || det.SubtotalUsd || det.precioUnitarioUsd || det.PrecioUnitarioUsd || 0);
        const idServicio = det.idServicio || det.IdServicio;
        const idProducto = det.idProducto || det.IdProducto;
        const nombreServicio = det.nombreServicio || det.NombreServicio || det.servicio?.nombre || '';
        const nombreProducto = det.nombreProducto || det.NombreProducto || det.producto?.nombre || '';
        const obs = det.observaciones || det.Observaciones || '';
        const uuid = (det.idRecepcionEquipoDetalle || det.IdRecepcionEquipoDetalle || Math.random()).toString();
        if (obs.toUpperCase().startsWith('REPUESTO:')) {
          return { uuid, tipo: 'repuesto', nombre: obs.replace(/^REPUESTO:\s*/i, '').trim(), precio_total: subtotal, cantidad: 1 };
        }
        if (idServicio) return { uuid, tipo: 'servicio', nombre: obs || nombreServicio || 'Servicio Técnico', precio_total: subtotal, cantidad };
        if (idProducto) return { uuid, tipo: 'producto', nombre: obs || nombreProducto || 'Producto', precio_total: subtotal, cantidad };
        if (subtotal > 0) return { uuid, tipo: 'servicio', nombre: obs || 'Servicio Técnico', precio_total: subtotal, cantidad };
        return null;
      }).filter(Boolean);
    },
    totalGeneralDetalle() { return this.itemsDetalle.reduce((sum, it) => sum + (parseFloat(it.precio_total) || 0), 0); },
    totalPagadoDetalle() { return this.pagosDetalle.reduce((sum, p) => sum + (parseFloat(p.montoUsd) || 0), 0); }
  },
  watch: {
    filtroNumeroOrden() { this.currentPage = 1; this.programarBusquedaAvanzada(); },
    filtroCedula() { this.currentPage = 1; this.programarBusquedaAvanzada(); },
    filtroNombre() { this.currentPage = 1; this.programarBusquedaAvanzada(); },
    filtroProducto() { this.currentPage = 1; this.programarBusquedaAvanzada(); },
    filtroEstado() { this.currentPage = 1; },
    currentPage() {
      this.$nextTick(() => {
        this.cargarPagosDeTodasLasOrdenes();
      });
    },
    'pagoForm.metodoPago'(nuevo) {
      if (!this.metodoRequiereCuentaBancaria) {
        this.pagoForm.idCuentaBancaria = null;
        this.pagoForm.idBanco = null;
      }
      if (nuevo !== 'PAGO_MOVIL') {
        this.pagoForm.telefonoPagoMovil = '';
        this.pagoForm.cedulaPagoMovil = '';
        this.pagoForm.idBanco = null;
      }
    }
  },
  async mounted() {
    await Promise.all([this.cargarCatalogos(), this.cargarRecepciones()]);
    this.cargarPagosGenerales();
    this.cargarPagosDeTodasLasOrdenes();
    this.$nextTick(() => {
      const existeEstado = this.estadosList.some(e => {
        const nombre = (e.nombre || '').toLowerCase();
        return nombre === 'por diagnosticar' || nombre.replace(/[_\s]/g, '') === 'pordiagnosticar';
      });
      if (!existeEstado && this.estadosList.length > 0) this.filtroEstado = 'TODOS';
    });
  },
  beforeUnmount() {
    if (this.debounceTimer) clearTimeout(this.debounceTimer);
    if (this.busquedaAbortController) this.busquedaAbortController.abort();
  },
  methods: {
    toTitleCase,
    formatearNumeroCuenta,
    goToFirstPage() { this.currentPage = 1; },
    goToLastPage() { this.currentPage = this.totalPages; },

    // ═══════════════════════════════════════════════════════
    // ✅ NUEVO: cargar pagos reales de cada orden
    // ═══════════════════════════════════════════════════════
    async cargarPagosDeTodasLasOrdenes() {
      // Optimización: Solo cargar pagos de la página actual visible, no de cientos de órdenes
      const visibles = this.paginatedRecepciones || [];
      if (!Array.isArray(visibles) || visibles.length === 0) return;
      const ids = visibles
        .map(r => r.idRecepcionEquipo || r.IdRecepcionEquipo)
        .filter(id => id && !this.pagosPorOrden[id] && !this.cargandoPagosPorOrden[id]);
      if (ids.length === 0) return;
      await Promise.all(ids.map(id => this.cargarPagosDeOrden(id)));
    },

    async cargarPagosDeOrden(idRecepcion) {
      if (!idRecepcion) return;
      if (this.cargandoPagosPorOrden[idRecepcion]) return;
      this.cargandoPagosPorOrden[idRecepcion] = true;
      try {
        let pagosRaw = [];
        // 1. Pagos en PagoSalidaEquipo
        try {
          const res1 = await obtenerPagosPorRecepcion(idRecepcion);
          if (res1?.success && Array.isArray(res1.data)) pagosRaw.push(...res1.data);
          else if (Array.isArray(res1)) pagosRaw.push(...res1);
        } catch (e1) {
          console.warn(`Error cargando pagos de salida orden ${idRecepcion}:`, e1);
        }



        const pagosUnificados = pagosRaw.map(p => {
          const estadoRaw = (p.estado ?? p.Estado ?? (p.idEstadoPago === 1 ? 'completado' : p.idEstadoPago === 2 ? 'pendiente' : '')).toString().toLowerCase().trim();
          return {
            idPago: p.idPago ?? p.IdPago ?? p.idPagoSalidaEquipo ?? p.IdPagoSalidaEquipo,
            estado: estadoRaw,
            montoUsd: parseFloat(p.montoUsd ?? p.MontoUsd ?? p.monto_usd ?? p.monto ?? 0) || 0,
            montoBs: parseFloat(p.montoBs ?? p.MontoBs ?? p.monto_bs ?? 0) || 0,
            metodoPago: p.metodoPago ?? p.MetodoPago ?? p.metodo_pago ?? '',
            referencia: p.referencia ?? p.Referencia ?? '',
            descripcion: p.descripcion ?? p.Descripcion ?? ''
          };
        });

        this.pagosPorOrden = {
          ...this.pagosPorOrden,
          [idRecepcion]: pagosUnificados
        };
      } catch (e) {
        console.warn(`No se pudieron cargar pagos de la orden ${idRecepcion}:`, e);
        this.pagosPorOrden = { ...this.pagosPorOrden, [idRecepcion]: [] };
      } finally {
        this.cargandoPagosPorOrden[idRecepcion] = false;
      }
    },

    // ═══════════════════════════════════════════════════════
    // ✅ NUEVO: helpers que reemplazan la lógica anterior
    // ═══════════════════════════════════════════════════════
        async cargarPagosGenerales() {
      try {
        const res = await obtenerPagos();
        const todos = res?.success && Array.isArray(res.data) ? res.data : (Array.isArray(res) ? res : []);
        this.pagosGenerales = todos;
      } catch (e) {
        console.warn('Error cargando pagos generales desde /Pago:', e);
        this.pagosGenerales = [];
      }
    },

    obtenerPagosDeItem(item) {
      if (!item) return [];
      const idRecepcion = String(item.idRecepcionEquipo || item.IdRecepcionEquipo || '');
      const numOrden = String(item.numeroOrden || item.NumeroOrden || '');

      // 1. Pagos cargados en pagosPorOrden (de salida de equipo)
      const pagosSalida = this.pagosPorOrden[idRecepcion] || [];

      // 2. Pagos de la tabla general /Pago (los que se administran en /pagos)
      const pagosTabla = (this.pagosGenerales || []).filter(p => {
        const pIdRec = String(p.idRecepcionEquipo || p.IdRecepcionEquipo || p.idRecepcion || p.IdRecepcion || p.idReferencia || p.IdReferencia || p.idDocumento || p.IdDocumento || '');
        const pCodRec = String(p.codigoRecepcion || p.CodigoRecepcion || p.codigo_recepcion || '');
        const pNumOrd = String(p.numeroOrden || p.NumeroOrden || p.numero_orden || '');
        const pDesc = String(p.descripcion || p.Descripcion || p.observaciones || p.Observaciones || '');

        if (idRecepcion && (pIdRec === idRecepcion || pCodRec === idRecepcion || pNumOrd === idRecepcion)) return true;
        if (numOrden && (pIdRec === numOrden || pCodRec === numOrden || pNumOrd === numOrden)) return true;
        if (numOrden && pDesc.includes(numOrden)) return true;
        return false;
      }).map(p => ({
        idPago: p.idPago ?? p.IdPago,
        estado: (p.estado ?? p.Estado ?? (p.idEstadoPago === 1 ? 'completado' : p.idEstadoPago === 2 ? 'pendiente' : '')).toString().toLowerCase().trim(),
        montoUsd: parseFloat(p.monto_usd ?? p.montoUsd ?? p.monto ?? 0) || 0,
        montoBs: parseFloat(p.monto_bs ?? p.montoBs ?? 0) || 0,
        metodoPago: p.metodo_pago ?? p.metodoPago ?? p.MetodoPago ?? '',
        referencia: p.referencia ?? p.Referencia ?? '',
        descripcion: p.descripcion ?? p.Descripcion ?? ''
      }));

      // Unificar priorizando el estado actualizado en /pagos
      const unificados = [...pagosSalida];
      for (const pt of pagosTabla) {
        const idx = unificados.findIndex(u => u.idPago && pt.idPago && u.idPago === pt.idPago);
        if (idx >= 0) {
          unificados[idx] = { ...unificados[idx], ...pt };
        } else {
          unificados.push(pt);
        }
      }
      return unificados;
    },

    obtenerMontoPagadoVerificado(item) {
      if (!item) return 0;
      const pagos = this.obtenerPagosDeItem(item);
      return +(pagos
        .filter(p => p.estado === 'completado')
        .reduce((s, p) => s + (p.montoUsd || 0), 0)).toFixed(2);
    },

    obtenerMontoPendienteVerificacion(item) {
      if (!item) return 0;
      const pagos = this.obtenerPagosDeItem(item);
      return +(pagos
        .filter(p => p.estado === 'pendiente')
        .reduce((s, p) => s + (p.montoUsd || 0), 0)).toFixed(2);
    },

    tienePagoPendienteVerificacion(item) {
      return this.obtenerMontoPendienteVerificacion(item) > 0;
    },

    // Determina si una orden pagada al 100% tiene pagos online pendientes de verificar en /pagos
    esPagoTotalmentePendienteVerificar(item) {
      if (!item) return false;
      if (!this.tienePagoPendienteVerificacion(item)) return false;
      const total = this.obtenerTotalNeto(item);
      if (total <= 0) return false;
      const pagado = this.obtenerMontoPagado(item);
      const pendiente = this.obtenerMontoPendienteVerificacion(item);
      return (pagado + pendiente) >= (total - 0.01) || this.esTotalmentePagado(item);
    },

    // Obtiene el monto pagado directo registrado en la recepción (adelanto / inicial / diagnóstico)
    obtenerMontoPagadoDirecto(item) {
      if (!item) return 0;
      const pagadoRep = parseFloat(
        item.montoPagadoReparacionUsd ??
        item.MontoPagadoReparacionUsd ??
        item.montoPagadoUsd ??
        item.MontoPagadoUsd ??
        item.montoPagado ??
        item.MontoPagado ??
        0
      ) || 0;
      const pagadoDiag = parseFloat(
        item.montoPagadoDiagnosticoUsd ??
        item.MontoPagadoDiagnosticoUsd ??
        0
      ) || 0;
      return +(pagadoRep + pagadoDiag).toFixed(2);
    },

    // Total pagado considerando pagos verificados en caja y montos registrados en la orden
    obtenerMontoPagado(item) {
      if (!item) return 0;
      const verificado = this.obtenerMontoPagadoVerificado(item);
      const directo = this.obtenerMontoPagadoDirecto(item);
      return +(Math.max(verificado, directo)).toFixed(2);
    },

    // Verifica si la orden está pagada al 100%
    esTotalmentePagado(item) {
      if (!item) return false;
      if (this.esSinCosto(item)) return true;
      const total = this.obtenerTotalNeto(item);
      const pagado = this.obtenerMontoPagado(item);
      if (total > 0 && pagado >= (total - 0.01)) return true;
      const tipo = ((item.tipoDiagnostico || item.TipoDiagnostico || '') + '').toUpperCase();
      if (tipo.includes('ADELANTA') && pagado >= (total - 0.01) && total > 0) return true;
      const est = (item.nombreEstado || item.NombreEstado || '').toLowerCase();
      if (est === 'entregado' && (pagado >= (total - 0.01) || item.fechaPago)) return true;
      return false;
    },

    // Verifica si la orden tiene abono parcial
    tieneAbono(item) {
      if (!item) return false;
      const pagado = this.obtenerMontoPagado(item);
      return pagado > 0 && !this.esTotalmentePagado(item);
    },

    // ═══════════════════════════════════════════════════════
    // Resto de métodos (sin cambios)
    // ═══════════════════════════════════════════════════════
    programarBusquedaAvanzada() {
      if (this.debounceTimer) clearTimeout(this.debounceTimer);
      this.debounceTimer = setTimeout(() => this.ejecutarBusquedaAvanzada(), 600);
    },

    async ejecutarBusquedaAvanzada() {
      const num = (this.filtroNumeroOrden || '').trim();
      const ced = (this.filtroCedula || '').trim();
      const nom = (this.filtroNombre || '').trim();
      const prod = (this.filtroProducto || '').trim();
      if (!num && !ced && !nom && !prod) {
        this.recepcionesBusqueda = null;
        this.buscandoApi = false;
        return;
      }
      if (this.busquedaAbortController) this.busquedaAbortController.abort();
      this.busquedaAbortController = new AbortController();
      const signal = this.busquedaAbortController.signal;
      this.buscandoApi = true;
      try {
        let res;
        if (num && !ced && !nom && !prod) res = await api.buscarPorNumeroOrden(num);
        else if (ced && !num && !nom && !prod) res = await api.buscarPorCedula(ced);
        else if (nom && !num && !ced && !prod) res = await api.buscarPorNombre(nom);
        else if (!prod) res = await api.buscarRecepcionesConFiltros(num, nom, ced);
        if (signal.aborted) return;
        let items = (res && res.success) ? res.data : this.fallbackLocal(num, ced, nom, prod);
        if (prod && res && res.success) {
          const p = prod.toLowerCase();
          items = items.filter(r =>
            (r.descripcion || '').toLowerCase().includes(p) ||
            (r.problema || '').toLowerCase().includes(p) ||
            (r.productoServicios?.nombre || '').toLowerCase().includes(p) ||
            (r.nombreProductoServicio || '').toLowerCase().includes(p) ||
            (r.repuestos || '').toLowerCase().includes(p)
          );
        }
        this.recepcionesBusqueda = items;
      } catch (error) {
        if (error.name === 'AbortError' || signal.aborted) return;
        console.error('Error en búsqueda avanzada:', error);
        this.recepcionesBusqueda = this.fallbackLocal(num, ced, nom, prod);
      } finally {
        if (!signal.aborted) this.buscandoApi = false;
      }
    },

    fallbackLocal(num, ced, nom, prod) {
      if (!this.recepciones || this.recepciones.length === 0) return [];
      let result = this.recepciones;
      if (num) result = result.filter(r => (r.numeroOrden || '').includes(num));
      if (ced) result = result.filter(r => (r.documentoCliente || '').toLowerCase().includes(ced.toLowerCase()));
      if (nom) {
        const t = nom.toLowerCase();
        result = result.filter(r => (r.nombreCliente || '').toLowerCase().includes(t));
      }
      if (prod) {
        const p = prod.toLowerCase();
        result = result.filter(r =>
          (r.descripcion || '').toLowerCase().includes(p) ||
          (r.problema || '').toLowerCase().includes(p) ||
          (r.productoServicios?.nombre || '').toLowerCase().includes(p) ||
          (r.nombreProductoServicio || '').toLowerCase().includes(p) ||
          (r.repuestos || '').toLowerCase().includes(p)
        );
      }
      return result.slice(0, 100);
    },

    async refrescarTodo() {
      await this.cargarRecepciones();
      await this.cargarPagosDeTodasLasOrdenes();  // ✅ NUEVO
      if (this.hayFiltrosActivos) await this.ejecutarBusquedaAvanzada();
    },

    limpiarFiltros() {
      if (this.busquedaAbortController) {
        this.busquedaAbortController.abort();
        this.busquedaAbortController = null;
      }
      this.filtroNumeroOrden = '';
      this.filtroCedula = '';
      this.filtroNombre = '';
      this.filtroProducto = '';
      this.filtroEstado = 'TODOS';
      this.recepcionesBusqueda = null;
      this.buscandoApi = false;
      this.currentPage = 1;
    },

    async verDetallesRecepcion(item) {
      this.tabActiva = 'resumen';
      this.recepcionSeleccionada = { ...item };
      this.pagosDetalle = [];
      this.showDetallesModal = true;
      try {
        const res = await api.obtenerRecepcionConRelaciones(item.idRecepcionEquipo);
        if (res.success && res.data) this.recepcionSeleccionada = { ...this.recepcionSeleccionada, ...res.data };
        try {
          const pagosRes = await obtenerPagosPorRecepcion(item.idRecepcionEquipo);
          if (pagosRes.success && pagosRes.data) this.pagosDetalle = pagosRes.data;
          else if (Array.isArray(pagosRes)) this.pagosDetalle = pagosRes;
        } catch (ePagos) {
          console.warn("No se pudieron cargar los pagos", ePagos);
        }
      } catch (error) {
        console.warn("No se pudieron cargar los detalles completos", error);
        this.toast.error("No se pudieron cargar los detalles completos");
      }
    },

    async generarPDFRecepcion(item) {
      try {
        this.toast.info(`Generando PDF para orden #${item.numeroOrden}...`);
        let ordenCompleta = { ...item };
        try {
          const res = await api.obtenerRecepcionConRelaciones(item.idRecepcionEquipo);
          if (res.success && res.data) ordenCompleta = res.data;
        } catch (e) { console.warn('No se pudieron cargar relaciones', e); }
        if (item.nombreTecnicoRecepcion && !ordenCompleta.nombreTecnicoRecepcion) ordenCompleta.nombreTecnicoRecepcion = item.nombreTecnicoRecepcion;
        if (item.nombreTecnicoReparador && !ordenCompleta.nombreTecnicoReparador) ordenCompleta.nombreTecnicoReparador = item.nombreTecnicoReparador;
        if (item.nombreTecnicoEntrega && !ordenCompleta.nombreTecnicoEntrega) ordenCompleta.nombreTecnicoEntrega = item.nombreTecnicoEntrega;
        await descargarOrdenServicioPDF(ordenCompleta);
        this.toast.success(`PDF generado para orden #${item.numeroOrden}`);
      } catch (error) {
        console.error('Error generando PDF:', error);
        this.toast.error('Error al generar el PDF: ' + (error.message || 'Error desconocido'));
      }
    },

    obtenerTelefonoCliente(item) {
      if (item.cliente && item.cliente.telefono) return item.cliente.telefono;
      if (item.telefonoCliente) return item.telefonoCliente;
      if (item.TelefonoCliente) return item.TelefonoCliente;
      if (item.telefono) return item.telefono;
      if (item.Telefono) return item.Telefono;
      return null;
    },

    abrirWhatsappCliente(item) {
      this.loading = true;
      let telefono = this.obtenerTelefonoCliente(item);
      if (!telefono && item.idCliente) {
        obtenerClientePorId(item.idCliente)
          .then(res => {
            if (res.success && res.data) {
              telefono = res.data.telefono || res.data.Telefono || '';
              this.enviarMensajeWhatsapp(item, telefono);
            } else { this.toast.error('No se pudo obtener el teléfono del cliente'); this.loading = false; }
          })
          .catch(err => { console.error('Error obteniendo cliente:', err); this.toast.error('Error al obtener datos del cliente'); this.loading = false; });
      } else { this.enviarMensajeWhatsapp(item, telefono); }
    },

    enviarMensajeWhatsapp(item, telefono) {
      if (!telefono) {
        this.toast.error(`El cliente ${item.nombreCliente || 'desconocido'} no tiene teléfono registrado.`);
        this.loading = false;
        return;
      }
      let cleanPhone = telefono.replace(/\D/g, '');
      if (cleanPhone.length === 10 && !cleanPhone.startsWith('58')) cleanPhone = '58' + cleanPhone;
      else if (cleanPhone.length === 11 && cleanPhone.startsWith('0')) cleanPhone = '58' + cleanPhone.substring(1);
      const nombreCliente = item.nombreCliente || 'Cliente';
      const ordenNum = item.numeroOrden || '';
      const estado = item.nombreEstado || '';
      const equipo = item.descripcion || 'Equipo';
      const total = (item.precioTotalUsd || 0).toFixed(2);
      let mensaje = `Hola ${nombreCliente}, le escribimos de Jean Informatico C.A.\n\n`;
      mensaje += `Orden: #${ordenNum}\nEquipo: ${equipo}\nEstado actual: ${estado.toUpperCase()}\nTotal: $${total}\n\n`;
      const estadoLower = (estado || '').toLowerCase();
      if (estadoLower.includes('reparado') || estadoLower.includes('listo')) mensaje += `Su equipo ya esta reparado y listo para retirar.\n`;
      else if (estadoLower.includes('diagnosticar') || estadoLower.includes('diagnostico')) mensaje += `Su equipo esta en proceso de diagnostico.\n`;
      else if (estadoLower.includes('imposible')) mensaje += `Lamentamos informarle que su equipo no tiene reparacion factible.\n`;
      else mensaje += `Estamos trabajando en su equipo, le avisaremos cualquier novedad.\n`;
      mensaje += `\nSede: ${item.nombreSede || item.taller || 'Sede Central'}\nContacto: 0416-7594723\n\nEste mensaje es automatico.`;
      window.open(`https://wa.me/${cleanPhone}?text=${encodeURIComponent(mensaje)}`, '_blank');
      this.toast.success(`Abriendo conversación con ${nombreCliente}`);
      this.loading = false;
    },

    exportarExcel() {
      try {
        if (this.recepciones.length === 0) { this.toast.warning('No hay datos para exportar'); return; }
        const datos = this.recepciones.map(item => {
          let telefono = this.obtenerTelefonoCliente(item) || '';
          return {
            'N° Orden': item.numeroOrden || '', 'Cliente': item.nombreCliente || '', 'Documento': item.documentoCliente || '',
            'Teléfono': telefono, 'Equipo': item.descripcion || '', 'Problema': item.problema || '', 'Estado': item.nombreEstado || '',
            'Fecha Ingreso': item.fechaIngreso ? new Date(item.fechaIngreso).toLocaleString('es-ES') : '',
            'Fecha Reparación': item.fechaReparacion ? new Date(item.fechaReparacion).toLocaleString('es-ES') : '',
            'Fecha Entrega': item.fechaEntrega ? new Date(item.fechaEntrega).toLocaleString('es-ES') : '',
            'Total USD': item.precioTotalUsd || 0, 'Descuento USD': item.descuento || 0,
            'Diagnóstico': item.tipoDiagnostico || 'GRATIS', 'Monto Diagnóstico': item.montoDiagnostico || 0,
            'Accesorios': item.accesorios || '', 'Sede': item.nombreSede || item.taller || '',
            'Técnico Recepción': item.nombreTecnicoRecepcion || item.NombreTecnicoRecepcion || '',
            'Técnico Reparador': item.nombreTecnicoReparador || item.NombreTecnicoReparador || '',
            'Técnico Entrega': item.nombreTecnicoEntrega || item.NombreTecnicoEntrega || '',
            'Método Pago': item.metodoPago || '', 'Referencia Pago': item.referenciaPago || '',
            'Monto Pagado Diagnóstico': item.montoPagadoDiagnosticoUsd || 0,
            'Monto Pagado Reparación': item.montoPagadoReparacionUsd || 0,
            'Actividades Realizadas': item.actividadesRealizadas || '', 'Repuestos': item.repuestos || '', 'Clave Patrón': item.clavePatron || ''
          };
        });
        const wb = XLSX.utils.book_new();
        const ws = XLSX.utils.json_to_sheet(datos);
        XLSX.utils.book_append_sheet(wb, ws, 'Recepciones');
        const fecha = new Date();
        const nombreArchivo = `Recepciones_${fecha.getFullYear()}-${String(fecha.getMonth()+1).padStart(2,'0')}-${String(fecha.getDate()).padStart(2,'0')}.xlsx`;
        XLSX.writeFile(wb, nombreArchivo);
        this.toast.success(`Exportados ${this.recepciones.length} registros a Excel`);
      } catch (error) { console.error('Error exportando Excel:', error); this.toast.error('Error al exportar: ' + error.message); }
    },

    async importarExcel(event) {
      const file = event.target.files[0];
      if (!file) return;
      this.importando = true;
      this.loading = true;
      try {
        const reader = new FileReader();
        reader.onload = async (e) => {
          try {
            const data = new Uint8Array(e.target.result);
            const workbook = XLSX.read(data, { type: 'array' });
            const firstSheet = workbook.Sheets[workbook.SheetNames[0]];
            const jsonData = XLSX.utils.sheet_to_json(firstSheet);
            if (jsonData.length === 0) { this.toast.warning('El archivo está vacío'); this.importando = false; this.loading = false; return; }
            this.mostrarConfirmacion({
              title: 'Importar Datos',
              message: `Se van a importar <b>${jsonData.length}</b> registros. ¿Deseas continuar?`,
              icon: 'mdi:file-upload', type: 'warning', confirmText: 'Sí, Importar', cancelText: 'Cancelar',
              onConfirm: async () => { await this.procesarImportacion(jsonData); },
              onCancel: () => { this.importando = false; this.loading = false; event.target.value = ''; }
            });
          } catch (error) {
            console.error('Error procesando archivo:', error);
            this.toast.error('Error al procesar el archivo: ' + error.message);
            this.importando = false; this.loading = false; event.target.value = '';
          }
        };
        reader.readAsArrayBuffer(file);
      } catch (error) {
        console.error('Error importando Excel:', error);
        this.toast.error('Error al importar: ' + error.message);
        this.importando = false; this.loading = false; event.target.value = '';
      }
    },

    async procesarImportacion(datos) {
      let importados = 0, errores = 0;
      try {
        const usuarioId = parseInt(localStorage.getItem('idUsuario') || 1);
        for (const row of datos) {
          try {
            const estadoNombre = (row['Estado'] || '').trim();
            let idEstado = 1;
            if (estadoNombre) {
              const estadoEncontrado = this.estadosList.find(e => e.nombre?.toLowerCase() === estadoNombre.toLowerCase());
              if (estadoEncontrado) idEstado = estadoEncontrado.idEstadoRecepcion;
            }
            const sedeNombre = (row['Sede'] || '').trim();
            let idSede = null;
            if (sedeNombre) {
              const sedeEncontrada = this.sedesList.find(s => s.nombre?.toLowerCase() === sedeNombre.toLowerCase());
              if (sedeEncontrada) idSede = sedeEncontrada.idSede;
            }
            const payload = {
              IdCliente: 1, IdUsuario: usuarioId,
              Problema: row['Problema'] || 'N/A', Descripcion: row['Equipo'] || 'N/A',
              Accesorios: row['Accesorios'] || 'Ninguno', IdSede: idSede, IdEstadoRecepcion: idEstado,
              ClavePatron: row['Clave Patrón'] || 'N/A', Taller: row['Sede'] || 'Sede Central',
              TipoDiagnostico: row['Diagnóstico'] || 'GRATIS', MontoDiagnostico: parseFloat(row['Monto Diagnóstico']) || 0,
              ActividadesRealizadas: row['Actividades Realizadas'] || '', Repuestos: row['Repuestos'] || '',
              FechaIngreso: row['Fecha Ingreso'] ? new Date(row['Fecha Ingreso']).toISOString() : new Date().toISOString()
            };
            const res = await api.crearRecepcion(payload);
            if (res.success) importados++; else errores++;
          } catch (error) { errores++; }
        }
        await this.cargarRecepciones();
        await this.cargarPagosDeTodasLasOrdenes();
        let mensaje = `Importación completada. ${importados} registros importados.`;
        if (errores > 0) mensaje += ` ${errores} con errores.`;
        this.toast.success(mensaje);
      } catch (error) {
        console.error('Error en procesarImportacion:', error);
        this.toast.error('Error al importar: ' + error.message);
      } finally {
        this.importando = false; this.loading = false;
        if (this.$refs.fileInput) this.$refs.fileInput.value = '';
      }
    },

    async cargarRecepciones() {
      this.loading = true;
      try {
        const res = await api.obtenerRecepciones();
        if (res.success) this.recepciones = res.data;
      } catch (error) {
        console.error("Error cargando recepciones", error);
        this.toast.error('Error al cargar los datos: ' + error.message);
      } finally { this.loading = false; }
    },

    async cargarCatalogos() {
      try {
        const [eRes, sRes, pRes, tRes, tecRes, sedRes] = await Promise.all([
          obtenerEstadosRecepcion(true), obtenerServicios('activos'), obtenerProductos('activos'),
          obtenerTasaVentaActual(), obtenerTecnicos(), obtenerSedesActivas()
        ]);
        if (eRes.success) this.estadosList = eRes.data;
        if (sRes.success) this.catalogoServicios = sRes.data;
        if (pRes.success) this.catalogoProductos = pRes.data;
        if (tRes.success) this.tasaActiva = tRes.data;
        if (tecRes.success) this.tecnicos = tecRes.data || [];
        if (sedRes.success) this.sedesList = sedRes.data || [];
      } catch (error) { console.error("Error catálogos", error); }
    },

    abrirEditarModal(item) {
      this.editItemRef = item;
      this.editForm = { idRecepcionEquipo: item.idRecepcionEquipo, numeroOrden: item.numeroOrden, accesorios: item.accesorios || '', idSede: item.idSede || null };
      this.showEditModal = true;
    },

    async guardarEdicion() {
      if (this.savingEdit) return;
      this.savingEdit = true;
      try {
        const item = this.editItemRef;
        const payload = {
          IdRecepcionEquipo: item.idRecepcionEquipo || item.IdRecepcionEquipo,
          NumeroOrden: item.numeroOrden || item.NumeroOrden,
          IdCliente: item.idCliente || item.IdCliente,
          IdUsuario: item.idUsuario || item.IdUsuario || parseInt(localStorage.getItem('idUsuario') || 1),
          Problema: item.problema || item.Problema || 'N/A',
          Descripcion: item.descripcion || item.Descripcion || 'N/A',
          Accesorios: this.editForm.accesorios || 'Ninguno',
          IdSede: this.editForm.idSede,
          IdEstadoRecepcion: item.idEstadoRecepcion || item.IdEstadoRecepcion,
          IdTecnicoAsignado: item.idTecnicoAsignado || item.IdTecnicoAsignado,
          IdTecnicoEntrega: item.idTecnicoEntrega || item.IdTecnicoEntrega,
          IdTecnicoRecepcion: item.idTecnicoRecepcion || item.IdTecnicoRecepcion,
          IdTecnicoReparador: item.idTecnicoReparador || item.IdTecnicoReparador,
          ClavePatron: item.clavePatron || item.ClavePatron || 'N/A',
          Taller: item.taller || item.Taller || 'Sede Central',
          TipoDiagnostico: item.tipoDiagnostico || item.TipoDiagnostico || 'Gratis (24-48h)',
          MontoDiagnostico: item.montoDiagnostico || item.MontoDiagnostico || 0,
          ActividadesRealizadas: item.actividadesRealizadas || item.ActividadesRealizadas || 'N/A',
          Repuestos: item.repuestos || item.Repuestos || '',
          FechaIngreso: item.fechaIngreso || item.FechaIngreso || new Date().toISOString(),
          IdProductoServicios: item.idProductoServicios || item.IdProductoServicios
        };
        const res = await api.actualizarRecepcion(item.idRecepcionEquipo, payload);
        if (res.success) {
          Object.assign(item, {
            accesorios: this.editForm.accesorios, idSede: this.editForm.idSede,
            nombreSede: this.sedesList.find(s => s.idSede === this.editForm.idSede)?.nombre || item.nombreSede
          });
          this.toast.success('Recepción actualizada correctamente');
          notificarRecepcion('Recepción actualizada', `Orden #${this.editForm.numeroOrden}`, `Accesorios: ${this.editForm.accesorios || 'N/A'}`);
          this.showEditModal = false;
          await this.refrescarTodo();
        } else { this.toast.error(res.error || 'No se pudo actualizar'); }
      } catch (e) { this.toast.error('Error: ' + e.message); }
      finally { this.savingEdit = false; }
    },

    obtenerNombreTecnicoLogueado() {
      if (this.entregaRapidaForm.nombreTecnico) return this.entregaRapidaForm.nombreTecnico;
      const user = getCurrentUser();
      if (user) {
        const tecEncontrado = this.tecnicos.find(t => t.idUsuario === user.idUsuario || t.IdUsuario === user.idUsuario);
        if (tecEncontrado) return `${tecEncontrado.nombre || tecEncontrado.Nombre} ${tecEncontrado.apellido || tecEncontrado.Apellido}`.trim() || 'Técnico';
        return user.nombre || user.username || 'Usuario logueado';
      }
      return 'Usuario no identificado';
    },

    async entregarSinPago(item) {
      const user = getCurrentUser();
      let idTecnico = null, nombreTecnico = '';
      if (user) {
        const tecEncontrado = this.tecnicos.find(t => t.idUsuario === user.idUsuario || t.IdUsuario === user.idUsuario);
        if (tecEncontrado) {
          idTecnico = tecEncontrado.idTecnico || tecEncontrado.IdTecnico;
          nombreTecnico = `${tecEncontrado.nombre || tecEncontrado.Nombre} ${tecEncontrado.apellido || tecEncontrado.Apellido}`.trim();
        } else { idTecnico = user.idUsuario || user.id; nombreTecnico = user.nombre || user.username || 'Usuario logueado'; }
      }
      if (!idTecnico) { idTecnico = parseInt(localStorage.getItem('idUsuario') || null); nombreTecnico = localStorage.getItem('nombre') || 'Usuario logueado'; }
      this.entregaRapidaForm.item = item;
      this.entregaRapidaForm.idTecnico = idTecnico;
      this.entregaRapidaForm.nombreTecnico = nombreTecnico || 'Técnico logueado';
      this.showEntregaRapidaModal = true;
    },

    async confirmarEntregaSinPago() {
      if (!this.entregaRapidaForm.idTecnico) { this.toast.error('No se pudo identificar al técnico.'); return; }
      this.loading = true;
      try {
        const item = this.entregaRapidaForm.item;
        const estadoEntregado = this.estadosList.find(e => (e.nombre || '').toUpperCase() === 'ENTREGADO');
        if (!estadoEntregado) throw new Error('No se encontró el estado "ENTREGADO".');
        const payload = { ...item, IdEstadoRecepcion: estadoEntregado.idEstadoRecepcion, IdTecnicoEntrega: this.entregaRapidaForm.idTecnico, FechaEntrega: new Date().toISOString() };
        const res = await api.actualizarRecepcion(item.idRecepcionEquipo, payload);
        if (res.success) {
          this.toast.success(`Orden #${item.numeroOrden} entregada correctamente.`);
          this.showEntregaRapidaModal = false;
          await this.refrescarTodo();
        } else { this.toast.error(res.error || 'No se pudo actualizar el estado.'); }
      } catch (error) { this.toast.error('Error: ' + error.message); }
      finally { this.loading = false; }
    },

    formatearFecha(fecha) { if (!fecha) return '---'; return new Date(fecha).toLocaleDateString('es-ES', { day: '2-digit', month: 'short', year: 'numeric' }); },
    formatearHora(fecha) { if (!fecha) return ''; return new Date(fecha).toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' }); },

    getEstadoStyle(estado) {
      if (!estado) return 'bg-gray-100 text-gray-800 border-gray-200 dark:bg-gray-900/40 dark:text-gray-300 dark:border-gray-800';
      const e = estado.toLowerCase();
      const estadoConfig = this.estadosList?.find(est => est.nombre?.toLowerCase() === e);
      if (estadoConfig && estadoConfig.color) {
        const colorMap = { 'success':'green','warning':'amber','danger':'red','info':'cyan','primary':'blue','purple':'purple','indigo':'indigo','pink':'pink','orange':'orange','teal':'teal','fuchsia':'fuchsia','lime':'lime','emerald':'emerald','rose':'rose','cyan':'cyan','sky':'sky','violet':'violet' };
        const baseColor = colorMap[estadoConfig.color.toLowerCase()] || 'gray';
        return `bg-${baseColor}-500 text-white border-${baseColor}-600 shadow-sm font-black`;
      }
      if (e.includes('diagno')) return 'bg-amber-500 text-white border-amber-600 shadow-sm font-black';
      if (e.includes('reparado')) return 'bg-green-500 text-white border-green-600 shadow-sm font-black';
      if (e.includes('entrega')) return 'bg-blue-500 text-white border-blue-600 shadow-sm font-black';
      if (e.includes('espera')) return 'bg-purple-500 text-white border-purple-600 shadow-sm font-black';
      return 'bg-gray-500 text-white border-gray-600 shadow-sm font-black';
    },

    async eliminarRegistro(item) {
      this.mostrarConfirmacion({
        title: '¿Eliminar Recepción?',
        message: `Estás a punto de borrar la orden <b>${item.numeroOrden}</b>.`,
        icon: 'mdi:alert-octagon', type: 'danger', confirmText: 'Sí, eliminar', cancelText: 'Cancelar',
        onConfirm: async () => {
          try {
            const res = await api.eliminarRecepcion(item.idRecepcionEquipo);
            if (res.success) { this.mostrarToastExito('Registro eliminado'); await this.refrescarTodo(); }
            else { this.mostrarToastError(res.error || 'No se pudo eliminar'); }
          } catch (error) { this.mostrarToastError('Error: ' + error.message); }
        }
      });
    },

    mostrarToastExito(mensaje) { this.toast.success(mensaje, { timeout: 3000 }); },
    mostrarToastError(mensaje) { this.toast.error(mensaje, { timeout: 5000 }); },

    mostrarConfirmacion(options) {
      this.confirmModalTitle = options.title || 'Confirmar';
      this.confirmModalMessage = options.message || '¿Estás seguro?';
      this.confirmModalIcon = options.icon || 'mdi:help-circle';
      this.confirmModalIconClass = options.iconClass || 'bg-blue-100 dark:bg-blue-900/30';
      this.confirmModalIconColor = options.iconColor || 'text-blue-600 dark:text-blue-400';
      this.confirmModalConfirmText = options.confirmText || 'Aceptar';
      this.confirmModalCancelText = options.cancelText || 'Cancelar';
      this.confirmModalType = options.type || 'confirm';
      if (options.type === 'danger') this.confirmModalConfirmClass = 'bg-gradient-to-r from-red-600 to-rose-600 hover:from-red-700 hover:to-rose-700';
      else if (options.type === 'success') this.confirmModalConfirmClass = 'bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700';
      else if (options.type === 'warning') this.confirmModalConfirmClass = 'bg-gradient-to-r from-yellow-600 to-amber-600 hover:from-yellow-700 hover:to-amber-700';
      else this.confirmModalConfirmClass = 'bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700';
      this.confirmCallback = options.onConfirm || null;
      this.confirmCancelCallback = options.onCancel || null;
      this.showConfirmModal = true;
    },

    cancelarConfirmacion() { this.showConfirmModal = false; if (this.confirmCancelCallback) this.confirmCancelCallback(); },
    aceptarConfirmacion() { this.showConfirmModal = false; if (this.confirmCallback) this.confirmCallback(); },

    esPorDiagnosticar(item) {
      const estado = ((item.nombreEstado || item.NombreEstado || '') + '').toLowerCase().replace(/_/g, ' ');
      const tipoDiag = ((item.tipoDiagnostico || item.TipoDiagnostico || '') + '').toLowerCase();
      return estado.includes('por diagnosticar') && !tipoDiag.includes('expre');
    },

    esSinCosto(item) {
      if (!item) return false;
      const estado = ((item.nombreEstado || item.NombreEstado || '') + '').toLowerCase().trim();
      return estado.includes('imposible') || estado.includes('funcionando') || estado.includes('sin fallo') || estado.includes('sin_fallo');
    },

    esEstadoPorDiagnosticar(item) {
      const estado = ((item?.nombreEstado || item?.NombreEstado || '') + '')
        .toLowerCase().replace(/_/g, ' ').trim();
      return estado.includes('por diagnosticar');
    },

    debeMostrarTotal(item) {
      if (!item) return false;
      const estado = ((item.nombreEstado || item.NombreEstado || '') + '').toLowerCase();
      const tipoDiag = ((item.tipoDiagnostico || item.TipoDiagnostico || '') + '').toLowerCase();
      const esExpress = tipoDiag.includes('expre') || (item.montoPagadoDiagnosticoUsd || 0) > 0;
      const esAdelantado = tipoDiag.includes('adelanta') || (item.montoPagadoReparacionUsd || 0) > 0 || (item.montoPagadoUsd || 0) > 0;
      if (esExpress || esAdelantado) return true;
      if ((item.precioTotalUsd || 0) > 0) return true;
      if (estado.includes('diagnost') || estado.includes('imposible') || estado.includes('funcionando')) return false;
      return true;
    },

    obtenerTotalNeto(item) {
      if (!item) return 0;
      const total = parseFloat(item.precioTotalUsd || 0);
      const desc = parseFloat(item.descuento || 0);
      return Math.max(0, total - desc);
    },

    obtenerSaldoRestante(item) {
      if (!item) return 0;
      const total = this.obtenerTotalNeto(item);
      const pagado = this.obtenerMontoPagado(item);
      return Math.max(0, +(total - pagado).toFixed(2));
    },

    esPagoAdelantado(item) {
      if (!item) return false;
      const tipo = ((item.tipoDiagnostico || item.TipoDiagnostico || '') + '').toUpperCase();
      if (tipo.includes('ADELANTA')) return true;
      const pagadoRep = parseFloat(item.montoPagadoReparacionUsd ?? item.MontoPagadoReparacionUsd ?? 0) || 0;
      return pagadoRep > 0;
    },

    async cargarCuentasBancarias() {
      this.cargandoCuentas = true;
      try {
        const [resCuentas, resBancosPM] = await Promise.all([
          obtenerCuentasPropias(),
          obtenerBancosPagoMovil()
        ]);
        if (resCuentas.success) this.cuentasPropias = resCuentas.data || [];
        if (resBancosPM.success) this.bancosPagoMovil = resBancosPM.data || [];
      } catch (e) {
        console.warn('No se pudieron cargar cuentas bancarias:', e);
      } finally {
        this.cargandoCuentas = false;
      }
    },

    async abrirModalPago(item) {
      this.pagoModalItem = item;
      const saldo = this.obtenerSaldoRestante(item);
      this.pagoForm = {
        montoUsd: saldo > 0 ? saldo : (item.precioTotalUsd || 0),
        metodoPago: 'PAGO_MOVIL',
        referencia: '',
        observaciones: `Pago online registrado - Orden #${item.numeroOrden}`,
        idCuentaBancaria: null,
        idBanco: null,
        telefonoPagoMovil: '',
        cedulaPagoMovil: ''
      };
      await this.cargarCuentasBancarias();
      this.showPagoModal = true;
    },

    cerrarModalPago() {
      this.showPagoModal = false;
      this.pagoModalItem = null;
      this.procesandoPago = false;
    },

    async confirmarRegistroPago() {
      if (!this.pagoModalItem) return;
      const monto = parseFloat(this.pagoForm.montoUsd || 0);
      if (monto <= 0) { this.toast.error('El monto a pagar debe ser mayor a 0'); return; }
      if (!this.pagoForm.metodoPago) { this.toast.error('Selecciona un método de pago'); return; }
      if (this.metodoRequiereCuentaBancaria && !this.pagoForm.idCuentaBancaria) {
        this.toast.warning('Selecciona la cuenta bancaria destino del pago'); return;
      }
      if (this.esPagoMovil && !this.pagoForm.idBanco) {
        this.toast.warning('Selecciona el banco del cliente para el Pago Móvil'); return;
      }
      if (this.metodoRequiereReferencia && (!this.pagoForm.referencia || !this.pagoForm.referencia.trim())) {
        this.toast.warning('Por favor ingresa el número de referencia para este método de pago online'); return;
      }

      this.procesandoPago = true;
      try {
        const item = this.pagoModalItem;
        const usuarioId = parseInt(localStorage.getItem('idUsuario') || '1');
        const idRecepcion = item.idRecepcionEquipo || item.IdRecepcionEquipo;
        const estadoRecepcionId = item.idEstadoRecepcion || item.IdEstadoRecepcion || 1;
        const tecnicoId = item.idTecnicoAsignado || item.IdTecnicoAsignado || item.idTecnicoRecepcion || item.IdTecnicoRecepcion || usuarioId;

        let idDetalle = null;
        try {
          const relRes = await api.obtenerRecepcionConRelaciones(idRecepcion);
          if (relRes?.success && relRes?.data) {
            const detalles = relRes.data.recepcionEquipoDetalles || relRes.data.detalles || [];
            if (detalles.length > 0) {
              idDetalle = detalles[0].idRecepcionEquipoDetalle || detalles[0].IdRecepcionEquipoDetalle;
            }
          }
        } catch (eDet) { console.warn('Error verificando detalles existentes:', eDet); }

        if (!idDetalle) {
          const detalleRes = await crearDetalleRecepcion(idRecepcion, {
            cantidad: 1, precioUnitarioUsd: monto, subtotalUsd: monto,
            precioUnitarioBs: 0, subtotalBs: 0,
            idTecnico: tecnicoId, idEstadoRecepcion: estadoRecepcionId,
            observaciones: this.pagoForm.observaciones || 'Pago registrado desde Recepción'
          });
          if (detalleRes?.success && detalleRes.data) {
            idDetalle = detalleRes.data.idRecepcionEquipoDetalle || detalleRes.data.IdRecepcionEquipoDetalle;
          }
        }

        let idSalida = null;
        const tasaData = this.tasaActiva || {};
        const tasaUsdBs = parseFloat(tasaData.PrecioUsdBs || tasaData.precioUsdBs || tasaData.precio_usd_bs || 1) || 1;
        const idFuenteTasa = parseInt(tasaData.IdFuenteTasa || tasaData.idFuenteTasa || 1);
        const idTasaVenta = parseInt(tasaData.IdTasaVenta || tasaData.idTasaVenta || 1);

        if (idDetalle) {
          const salidaRes = await crearSalidaEquipo({
            precioUnitarioUsd: monto, subtotalUsd: monto,
            precioUnitarioBs: +(monto * tasaUsdBs).toFixed(2),
            subtotalBs: +(monto * tasaUsdBs).toFixed(2),
            idRecepcionEquipoDetalle: parseInt(idDetalle),
            idUsuario: usuarioId, idEstadoRecepcion: estadoRecepcionId
          });
          if (salidaRes?.success && salidaRes.data) {
            idSalida = salidaRes.data.idSalidaEquipoDetalle || salidaRes.data.IdSalidaEquipoDetalle;
          }
        }

        const totalNeto = this.obtenerTotalNeto(item);
        const pagadoAnterior = this.obtenerMontoPagado(item);
        const nuevoTotalPagado = pagadoAnterior + monto;
        const saldoPendiente = Math.max(0, +(totalNeto - nuevoTotalPagado).toFixed(2));
        const tipoPagoNombre = (nuevoTotalPagado >= (totalNeto - 0.01) && totalNeto > 0) ? 'Completo' : 'Abono';

        if (idSalida) {
          await crearPagoSalidaEquipo({
            idSalidaEquipoDetalle: idSalida,
            numeroRecibo: `PAG-${item.numeroOrden || idRecepcion}`,
            fechaPago: new Date().toISOString(),
            montoUsd: monto, montoBs: +(monto * tasaUsdBs).toFixed(2),
            tasaBsUsd: tasaUsdBs, idFuenteTasa, idTasaVenta,
            tipoPago: tipoPagoNombre, saldoPendienteUsd: saldoPendiente,
            metodoPago: this.pagoForm.metodoPago,
            referencia: this.pagoForm.referencia || 'S/R',
            idUsuario: usuarioId, estado: 'Completado',
            observaciones: this.pagoForm.observaciones || `Pago registrado (${tipoPagoNombre}) - Orden #${item.numeroOrden}`
          });
        }

        if (this.metodoRequiereCuentaBancaria && this.pagoForm.idCuentaBancaria) {
          try {
            const cuentaSel = this.cuentasPropias.find(
              c => (c.idCuentaBancaria || c.IdCuentaBancaria) === this.pagoForm.idCuentaBancaria
            );
            const bancoEmisor = this.bancosPagoMovil.find(
              b => (b.idBanco || b.IdBanco) === this.pagoForm.idBanco
            );
            await crearPagoCuenta({
              tipoDocumentoPago: 'salida_equipo',
              idDocumentoPago: idSalida || idRecepcion,
              idCuentaBancaria: this.pagoForm.idCuentaBancaria,
              montoUsd: monto, montoBs: +(monto * tasaUsdBs).toFixed(2),
              tasaBsUsd: tasaUsdBs,
              metodoPago: this.pagoForm.metodoPago.toLowerCase(),
              referencia: this.pagoForm.referencia || 'S/R',
              fechaPago: new Date().toISOString(),
              pmBancoEmisorCodigo: this.esPagoMovil ? (bancoEmisor?.codigo || bancoEmisor?.Codigo || null) : null,
              pmBancoReceptorCodigo: this.esPagoMovil ? (cuentaSel?.pmBancoCodigo || cuentaSel?.bancoCodigo || null) : null,
              pmTelefonoEmisor: this.esPagoMovil ? this.pagoForm.telefonoPagoMovil : null,
              pmDocumentoEmisor: this.esPagoMovil ? this.pagoForm.cedulaPagoMovil : null,
              pmCodigoOperacion: this.esPagoMovil ? this.pagoForm.referencia : null,
              observaciones: this.pagoForm.observaciones || `Pago orden #${item.numeroOrden}`
            });
          } catch (eCuenta) {
            console.warn('No se pudo registrar el pago en la cuenta bancaria:', eCuenta);
            this.toast.warning('El pago se registró, pero no se pudo asociar a la cuenta bancaria');
          }
        }

        const nuevoMontoPagadoReparacion = parseFloat(item.montoPagadoReparacionUsd || 0) + monto;
        const updatePayload = {
          IdRecepcionEquipo: idRecepcion,
          NumeroOrden: item.numeroOrden || item.NumeroOrden,
          IdProductoServicios: item.idProductoServicios || item.IdProductoServicios,
          FechaIngreso: item.fechaIngreso || item.FechaIngreso,
          FechaReparacion: item.fechaReparacion || item.FechaReparacion,
          FechaEntrega: item.fechaEntrega || item.FechaEntrega,
          FechaPago: new Date().toISOString(),
          Problema: item.problema || item.Problema || 'N/A',
          Descripcion: item.descripcion || item.Descripcion || 'N/A',
          Repuestos: item.repuestos || item.Repuestos || '',
          Accesorios: item.accesorios || item.Accesorios || 'Ninguno',
          IdCliente: item.idCliente || item.IdCliente,
          IdUsuario: item.idUsuario || item.IdUsuario || usuarioId,
          IdTecnicoAsignado: item.idTecnicoAsignado || item.IdTecnicoAsignado,
          IdTecnicoEntrega: item.idTecnicoEntrega || item.IdTecnicoEntrega,
          IdTecnicoRecepcion: item.idTecnicoRecepcion || item.IdTecnicoRecepcion,
          IdTecnicoReparador: item.idTecnicoReparador || item.IdTecnicoReparador,
          ClavePatron: item.clavePatron || item.ClavePatron || 'N/A',
          Taller: item.taller || item.Taller || 'Sede Central',
          TipoDiagnostico: item.tipoDiagnostico || item.TipoDiagnostico || 'ADELANTADO',
          MontoDiagnostico: item.montoDiagnostico || 0,
          PrecioTotalUsd: item.precioTotalUsd || (totalNeto > 0 ? totalNeto : monto),
          MontoPagadoReparacionUsd: nuevoMontoPagadoReparacion,
          MetodoPago: this.pagoForm.metodoPago,
          Referencia: this.pagoForm.referencia || item.referencia || '',
          ActividadesRealizadas: item.actividadesRealizadas || '',
          IdEstadoRecepcion: estadoRecepcionId,
          IdSede: item.idSede || item.IdSede
        };

        const resUpdate = await api.actualizarRecepcion(idRecepcion, updatePayload);
        if (!resUpdate.success) {
          console.warn('Aviso al actualizar orden:', resUpdate.error);
        }

        const metodoLabel = this.metodosPago.find(m => m.value === this.pagoForm.metodoPago)?.label || this.pagoForm.metodoPago;
        this.toast.success(`✅ Pago de $${monto.toFixed(2)} registrado con éxito (${metodoLabel})`);
        notificarRecepcion('Pago Registrado', `Orden #${item.numeroOrden}`, `Monto: $${monto.toFixed(2)} (${metodoLabel})`);

        this.cerrarModalPago();
        await this.refrescarTodo();
      } catch (err) {
        console.error('Error al registrar pago:', err);
        this.toast.error('Error al registrar el pago: ' + (err.message || err));
      } finally {
        this.procesandoPago = false;
      }
    },

    abrirPagoExpress(item) { this.pagoExpressItem = item; this.pagoExpress = { metodoPago: '', referencia: '' }; this.showPagoExpressModal = true; },
    cancelarPagoExpress() { this.showPagoExpressModal = false; this.pagoExpressItem = null; this.pagoExpress = { metodoPago: '', referencia: '' }; },

    async confirmarPagoExpress() {
      if (!this.pagoExpress.metodoPago) { this.toast.error('Selecciona un método de pago.'); return; }
      const item = this.pagoExpressItem;
      if (!item) return;
      this.procesandoPagoExpress = true;
      try {
        const usuarioId = parseInt(localStorage.getItem('idUsuario') || '1');
        const idRecepcion = item.idRecepcionEquipo || item.IdRecepcionEquipo;
        const estadoRecepcionId = item.idEstadoRecepcion || item.IdEstadoRecepcion || 1;
        const tecnicoId = item.idTecnicoAsignado || item.IdTecnicoAsignado || item.idTecnicoRecepcion || item.IdTecnicoRecepcion || item.idUsuario || item.IdUsuario || usuarioId;
        const actualizarPayload = {
          IdRecepcionEquipo: idRecepcion, NumeroOrden: item.numeroOrden || item.NumeroOrden,
          IdProductoServicios: item.idProductoServicios || item.IdProductoServicios,
          FechaIngreso: item.fechaIngreso || item.FechaIngreso, FechaReparacion: item.fechaReparacion || item.FechaReparacion,
          FechaEntrega: item.fechaEntrega || item.FechaEntrega, FechaPago: item.fechaPago || item.FechaPago,
          Problema: item.problema || item.Problema || 'N/A', Descripcion: item.descripcion || item.Descripcion || 'N/A',
          Repuestos: item.repuestos || item.Repuestos || '', Accesorios: item.accesorios || item.Accesorios || 'Ninguno',
          IdCliente: item.idCliente || item.IdCliente, IdUsuario: item.idUsuario || item.IdUsuario || usuarioId,
          IdTecnicoAsignado: item.idTecnicoAsignado || item.IdTecnicoAsignado,
          IdTecnicoEntrega: item.idTecnicoEntrega || item.IdTecnicoEntrega,
          IdTecnicoRecepcion: item.idTecnicoRecepcion || item.IdTecnicoRecepcion,
          IdTecnicoReparador: item.idTecnicoReparador || item.IdTecnicoReparador,
          ClavePatron: item.clavePatron || item.ClavePatron || 'N/A',
          Taller: item.taller || item.Taller || 'Sede Central',
          TipoDiagnostico: 'EXPRESS', MontoDiagnostico: 10,
          ActividadesRealizadas: item.actividadesRealizadas || item.ActividadesRealizadas || '',
          IdEstadoRecepcion: estadoRecepcionId, IdSede: item.idSede || item.IdSede
        };
        const resUpdate = await api.actualizarRecepcion(idRecepcion, actualizarPayload);
        if (!resUpdate.success) throw new Error(resUpdate.error || 'No se pudo actualizar la orden.');
        const detalleRes = await crearDetalleRecepcion(idRecepcion, { cantidad: 1, precioUnitarioUsd: 0, subtotalUsd: 0, precioUnitarioBs: 0, subtotalBs: 0, idTecnico: tecnicoId, idEstadoRecepcion: estadoRecepcionId, observaciones: 'Diagnóstico Express' });
        if (!detalleRes.success) throw new Error(detalleRes.error || 'No se pudo crear el detalle.');
        const idDetalle = detalleRes.data?.idRecepcionEquipoDetalle || detalleRes.data?.IdRecepcionEquipoDetalle;
        if (!idDetalle) throw new Error('No se encontró el detalle generado.');
        const salidaRes = await crearSalidaEquipo({ precioUnitarioUsd: 0, precioUnitarioBs: 0, idRecepcionEquipoDetalle: idDetalle, idUsuario: usuarioId, idEstadoRecepcion: estadoRecepcionId });
        if (!salidaRes.success) throw new Error(salidaRes.error || 'No se pudo crear la salida.');
        const idSalida = salidaRes.data?.idSalidaEquipoDetalle || salidaRes.data?.IdSalidaEquipoDetalle;
        if (!idSalida) throw new Error('No se obtuvo la salida generada.');
        const tasaRes = await obtenerTasaVentaActual();
        const tasaData = tasaRes.success && tasaRes.data ? tasaRes.data : {};
        const tasaUsdBs = parseFloat(tasaData.PrecioUsdBs || tasaData.precioUsdBs || 1) || 1;
        const idFuenteTasa = tasaData.IdFuenteTasa || tasaData.idFuenteTasa || 1;
        const idTasaVenta = tasaData.IdTasaVenta || tasaData.idTasaVenta || 1;
        const pagoRes = await crearPagoSalidaEquipo({
          idSalidaEquipoDetalle: idSalida,
          numeroRecibo: `DIAG-${item.numeroOrden || item.NumeroOrden || idRecepcion}`,
          fechaPago: new Date().toISOString(),
          montoUsd: 10, montoBs: +(10 * tasaUsdBs).toFixed(2), tasaBsUsd: tasaUsdBs,
          idFuenteTasa: parseInt(idFuenteTasa, 10), idTasaVenta: parseInt(idTasaVenta, 10),
          tipoPago: 'EXPRESS', metodoPago: this.pagoExpress.metodoPago,
          referencia: this.pagoExpress.referencia || null, idUsuario: usuarioId,
          estado: 'Completado', observaciones: 'Pago Diagnóstico Express'
        });
        if (!pagoRes.success) throw new Error(pagoRes.error || 'No se pudo registrar el pago.');
        this.toast.success(`Orden #${item.numeroOrden || item.NumeroOrden} actualizada a Express.`);
        this.cancelarPagoExpress();
        await this.refrescarTodo();
      } catch (error) { console.error('Error en confirmarPagoExpress:', error); this.toast.error('Error: ' + (error.message || error)); }
      finally { this.procesandoPagoExpress = false; }
    },

    async ponerEnGarantia(item) {
      this.mostrarConfirmacion({
        title: '¿Pasar a Garantía?', message: `¿Poner la orden <b>${item.numeroOrden}</b> en Garantía?`,
        icon: 'mdi:shield-alert', type: 'warning', confirmText: 'Sí, pasar a Garantía',
        onConfirm: async () => {
          this.loading = true;
          try {
            const estadoGarantia = this.estadosList.find(e => (e.nombre || '').toLowerCase().includes('garantia') || (e.nombre || '').toLowerCase().includes('garantía'));
            if (!estadoGarantia) throw new Error('No se encontró el estado "GARANTÍA".');
            const res = await api.cambiarEstadoSimple(item.idRecepcionEquipo, estadoGarantia.idEstadoRecepcion, 'Pasado a Garantía');
            if (res.success) { this.toast.success(`Orden #${item.numeroOrden} ahora está en GARANTÍA.`); await this.refrescarTodo(); }
            else { this.toast.error(res.error || 'No se pudo actualizar.'); }
          } catch (error) { this.toast.error('Error: ' + error.message); }
          finally { this.loading = false; }
        }
      });
    },

    async finalizarGarantia(item) {
      this.mostrarConfirmacion({
        title: '¿Finalizar Garantía?', message: `¿Finalizar la garantía y entregar la orden <b>${item.numeroOrden}</b>?`,
        icon: 'mdi:check-circle', type: 'success', confirmText: 'Sí, finalizar y entregar',
        onConfirm: async () => {
          this.loading = true;
          try {
            const estadoEntregado = this.estadosList.find(e => (e.nombre || '').toLowerCase().includes('entregado'));
            if (!estadoEntregado) throw new Error('No se encontró el estado "ENTREGADO".');
            const res = await api.cambiarEstadoSimple(item.idRecepcionEquipo, estadoEntregado.idEstadoRecepcion, 'Garantía Finalizada');
            if (res.success) { this.toast.success(`Garantía de la orden #${item.numeroOrden} finalizada.`); await this.refrescarTodo(); }
            else { this.toast.error(res.error || 'No se pudo finalizar.'); }
          } catch (error) { this.toast.error('Error: ' + error.message); }
          finally { this.loading = false; }
        }
      });
    },

    async imprimirTicketLocal(item) {
      this.toast.info(`Generando ticket para orden ${item.numeroOrden}...`);
      let ordenCompleta = { ...item };
      let pagoInfo = null;
      try {
        const res = await api.obtenerRecepcionConRelaciones(item.idRecepcionEquipo);
        if (res.success && res.data) ordenCompleta = res.data;
        try {
          const { obtenerPagosPorSalidaDetalle } = await import('@/api/pagoSalidaEquipo.js');
          if (ordenCompleta.detalles && ordenCompleta.detalles.length > 0) {
            for (const detalle of ordenCompleta.detalles) {
              if (detalle.salidasEquipoDetalle && detalle.salidasEquipoDetalle.length > 0) {
                for (const salida of detalle.salidasEquipoDetalle) {
                  if (salida.idSalidaEquipoDetalle) {
                    const pagosRes = await obtenerPagosPorSalidaDetalle(salida.idSalidaEquipoDetalle);
                    if (pagosRes.success && pagosRes.data && pagosRes.data.length > 0) {
                      const pago = pagosRes.data[0];
                      pagoInfo = { metodoPago: pago.metodoPago || pago.metodo_pago || pago.MetodoPago, referencia: pago.referencia || pago.Referencia, montoPagado: pago.montoUsd || pago.MontoUsd, fechaPago: pago.fechaPago || pago.FechaPago };
                      break;
                    }
                  }
                }
              }
              if (pagoInfo) break;
            }
          }
        } catch (e) { console.warn("No se pudo obtener info de pago:", e); }
      } catch (e) { console.warn("No se pudieron cargar detalles para el ticket", e); }
      let clienteObj = ordenCompleta.cliente || {};
      if ((!clienteObj.telefono || !clienteObj.nombre) && (ordenCompleta.idCliente || item.idCliente)) {
        try {
          const resCli = await obtenerClientePorId(ordenCompleta.idCliente || item.idCliente);
          if (resCli.success && resCli.data) clienteObj = resCli.data;
        } catch (e) { console.warn("Error cargando cliente extra", e); }
      }
      const nombreFull = clienteObj.nombre ? `${clienteObj.nombre} ${clienteObj.apellido || ''}`.trim() : (ordenCompleta.nombreCliente || item.nombreCliente);
      const cliente = { nombre: nombreFull, documento: clienteObj.documento || ordenCompleta.documentoCliente || item.documentoCliente, telefono: clienteObj.telefono || ordenCompleta.telefonoCliente || item.telefonoCliente || 'S/D' };
      const nombreEquipo = ordenCompleta.productoServicios?.nombre || ordenCompleta.nombreProductoServicio || item.nombreProductoServicio || 'EQUIPO';
      let descLimpia = ordenCompleta.descripcion || item.descripcion || 'NO ESPECIFICADO';
      if (nombreEquipo && nombreEquipo !== 'EQUIPO' && descLimpia.toUpperCase().startsWith(nombreEquipo.toUpperCase())) descLimpia = descLimpia.substring(nombreEquipo.length).trim();
      const detalles = ordenCompleta.detalles || [];
      const servicios = [], productos = [];
      detalles.forEach(det => {
        const precio = parseFloat(det.precioUnitarioUsd || det.subtotalUsd || 0);
        const garantia = det.garantiaDias || 0;
        if (det.idServicio) servicios.push({ nombre: det.nombreServicio || det.observaciones || 'Servicio', precio, garantia, observaciones: det.observaciones });
        else if (det.idProducto) productos.push({ nombre: det.nombreProducto || det.nombre || 'Producto', precio, garantia });
        else if (det.observaciones && det.observaciones.startsWith('REPUESTO:')) servicios.push({ nombre: det.observaciones.replace('REPUESTO:', '').trim(), precio, garantia: 0, observaciones: det.observaciones });
      });
      const ordenParaTicket = {
        ...ordenCompleta, ...item, equipo: nombreEquipo, descripcion: descLimpia,
        fechaIngreso: ordenCompleta.fechaIngreso || item.fechaIngreso, fechaEntrega: ordenCompleta.fechaEntrega || item.fechaEntrega,
        fechaPago: ordenCompleta.fechaPago || item.fechaPago || (pagoInfo?.fechaPago),
        montoPagadoUsd: this.obtenerMontoPagado(item) || ordenCompleta.montoPagadoUsd || item.montoPagadoUsd || pagoInfo?.montoPagado || (item.precioTotalUsd || 0),
        metodoPago: pagoInfo?.metodoPago || ordenCompleta.metodoPago || item.metodoPago || item.MetodoPago,
        referenciaPago: pagoInfo?.referencia || ordenCompleta.referenciaPago || item.referenciaPago || item.Referencia,
        servicios, productos, garantiaDias: ordenCompleta.garantiaDias || item.garantiaDias || 0,
        tasaBsUsd: ordenCompleta.tasaBsUsd || item.tasaBsUsd || this.tasaActiva?.precio_usd_bs || 1,
        descuento: ordenCompleta.descuento || item.descuento || 0
      };
      const tecnico = { nombre: ordenParaTicket.nombreTecnicoRecepcion || item.nombreTecnicoRecepcion || 'TÉCNICO' };
      imprimirTicket(ordenParaTicket, cliente, tecnico, pagoInfo);
    }
  }
};
</script>

<style scoped>
.text-lexend { font-family: 'Lexend', sans-serif; }
.custom-scrollbar::-webkit-scrollbar { height: 6px; width: 6px; }
.custom-scrollbar::-webkit-scrollbar-track { background: transparent; }
.custom-scrollbar::-webkit-scrollbar-thumb { background: #e2e8f0; border-radius: 10px; }
.dark .custom-scrollbar::-webkit-scrollbar-thumb { background: #334155; }

@keyframes zoomIn { from { opacity: 0; transform: scale(0.95); } to { opacity: 1; transform: scale(1); } }
.animate-zoomIn { animation: zoomIn 0.2s ease-out; }

@keyframes fadeInUp { from { opacity: 0; transform: translateY(15px); } to { opacity: 1; transform: translateY(0); } }
.animate-fadeInUp { animation: fadeInUp 0.4s ease-out forwards; }
.delay-100 { animation-delay: 0.1s; }
</style>
